import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import supabase from '../config/supabase';
import { useVendorDirectory } from '../utils/useVendorDirectory';
import { propagateGlobalCountFieldsToParents, formatPropagationToast } from '../services/sessionFieldPropagation';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import { fetchCountLadders } from '../services/purchaseUnitsService';
import {
  fetchSelectedOptionsForCountItems,
  setSelectedPurchaseOptionForLines,
  setCountFormatForLines,
  setCountFormatForTemplateItems,
} from '../services/countItemSelectionService';
import VendorSelectPopover from './VendorSelectPopover';
import ParUnitControl from './ParUnitControl';
import CountFormatControl from './CountFormatControl';
import ParTransitionPrompt from './ParTransitionPrompt';
import { resolveCountPresentation, fallbackCountLadder } from '../utils/countPresentation';
import { detectParTransition, parTransitionKey } from '../utils/parTransition';
import {
  fetchParTransitions,
  getParRecommendation,
  recordParTransition,
  applyExactEquivalentRefresh,
} from '../services/parTransitionService';
import { resolveRowPar } from '../utils/countUnitModel.js';
import { canonicalLinkage, purchaseOptionsSummary, costCellForSummary } from '../utils/itemCostSummary';
import { groupIdentityFor, groupMembersFor, expandOptionsAcrossGroup } from '../utils/relatedItemGroups';
import { fetchWorkingSetGroupsForStore } from '../services/purchasingPlanService';
// location_position is now confirmed present in count_items; keep as let for legacy guard.
let countItemsSupportsLocationPosition = true;

const normalizeLocationKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Group-aware when the store's working-set groups are supplied: a related
// group is ONE countable thing, so an AI operation addressed to any member
// matches every member's row at that location (relatedItemGroups
// groupIdentityFor; the count-through's identity rule).
const rowAiKey = (row, groups = null) =>
  `${groupIdentityFor(groups, row.inventory_item_id || row.id)}::${normalizeLocationKey(row.location)}`;

// Flatten grouped items into a flat row list the AI panel can reason about.
// Preserves count_item_id so the apply diff can detect removals.
export const flattenGroupedForAi = (grouped = {}) => {
  const rows = [];
  Object.entries(grouped || {}).forEach(([location, items]) => {
    (items || []).forEach((item) => {
      const snap = item.item_snapshot || {};
      rows.push({
        inventory_item_id: item.inventory_item_id || item.id,
        id: item.inventory_item_id || item.id,
        count_item_id: item.count_item_id || null,
        description: snap.description || item.description || snap.name || item.name || '',
        name: snap.name || item.name || snap.description || item.description || '',
        category: snap.category || item.category || '',
        vendor: snap.vendor || item.vendor || '',
        brand: snap.brand || item.brand || '',
        location,
        par_level: item.par_level ?? null,
        par_defaulted: (item.par_defaulted ?? snap.par_defaulted) === true,
        par_unit: item.par_unit || snap.unit || item.unit || 'each',
        min_order_qty: item.min_order_qty ?? item.moq ?? null,
        min_order_unit: item.min_order_unit || item.moq_unit || snap.unit || item.unit || 'each',
        unit: snap.unit || item.unit || 'each',
        count_position: item.count_position ?? null,
        item_snapshot: item.item_snapshot,
        _original: item,
      });
    });
  });
  return rows;
};

// Apply approved AI operations to a flat row list. Mirrors the behavior of
// GroupEditor.applyAiOperationsToRows so both mount points stay in sync.
export const applyAiOpsToRows = (prevRows, approvedOps, workingSetGroups = null) => {
  let next = prevRows.map((row) => ({ ...row }));
  const findIdx = (invId, loc) =>
    next.findIndex((r) => (
      (r.inventory_item_id || r.id) === invId
      && normalizeLocationKey(r.location) === normalizeLocationKey(loc)
    ));

  (approvedOps || []).forEach((op) => {
    const r = op.resolution || {};
    if (op.type === 'add_items') {
      (r.additions || []).forEach((addition) => {
        if (findIdx(addition.inventory_item_id, addition.target_location) >= 0) return;
        next.push({
          id: addition.inventory_item_id,
          inventory_item_id: addition.inventory_item_id,
          count_item_id: null,
          description: addition.item_name,
          name: addition.item_name,
          category: addition.category,
          vendor: addition.vendor,
          brand: addition.brand,
          location: addition.target_location,
          par_level: addition.par_level ?? null,
          par_defaulted: addition.par_defaulted === true,
          par_unit: addition.unit || 'each',
          min_order_qty: addition.min_order_qty ?? null,
          min_order_unit: addition.min_order_unit || addition.unit || 'each',
          unit: addition.unit || 'each',
          count_position: next.length + 1,
          _ai_added: true,
        });
      });
    } else if (op.type === 'remove_items') {
      const keys = new Set((r.removals || []).map((row) => rowAiKey(row, workingSetGroups)));
      next = next.filter((row) => !keys.has(rowAiKey(row, workingSetGroups)));
    } else if (op.type === 'move_items') {
      (r.transfers || []).forEach((t) => {
        const sourceIdx = findIdx(t.inventory_item_id, t.from_location);
        if (sourceIdx < 0) return;
        const existsAtTargetIdx = findIdx(t.inventory_item_id, t.to_location);
        if (existsAtTargetIdx >= 0) {
          next.splice(sourceIdx, 1);
        } else {
          next[sourceIdx] = { ...next[sourceIdx], location: t.to_location };
        }
      });
    } else if (op.type === 'copy_items') {
      (r.transfers || []).forEach((t) => {
        if (findIdx(t.inventory_item_id, t.to_location) >= 0) return;
        const sourceIdx = findIdx(t.inventory_item_id, t.from_location);
        if (sourceIdx < 0) return;
        const src = next[sourceIdx];
        next.push({
          ...src,
          count_item_id: null,
          location: t.to_location,
          _ai_added: true,
          _original: undefined,
        });
      });
    } else if (op.type === 'rename_location') {
      const fromKey = normalizeLocationKey(r.from_location || '');
      const toLoc = r.to_location || '';
      const targetKey = normalizeLocationKey(toLoc);
      const sourceRows = next.filter((row) => normalizeLocationKey(row.location) === fromKey);
      sourceRows.forEach((row) => {
        const invId = row.inventory_item_id || row.id;
        const existsAtTarget = next.some((other) => (
          other !== row
          && (other.inventory_item_id || other.id) === invId
          && normalizeLocationKey(other.location) === targetKey
        ));
        if (existsAtTarget) {
          next = next.filter((candidate) => candidate !== row);
        } else {
          row.location = toLoc;
        }
      });
    } else if (op.type === 'set_par') {
      const keys = new Set((r.updates || []).map((u) => rowAiKey(u, workingSetGroups)));
      // par_unit is optional: the AI PAR-suggestion flow sends it (bottle / can /
      // keg) so the saved row matches the unit shown in the review console; plain
      // "set par to N" edits omit it and leave the existing unit untouched.
      next = next.map((row) => keys.has(rowAiKey(row, workingSetGroups))
        ? {
          ...row,
          par_level: r.par_value,
          par_defaulted: false,
          ...(r.par_unit ? { par_unit: r.par_unit } : {}),
        }
        : row);
    } else if (op.type === 'set_moq') {
      const keys = new Set((r.updates || []).map((u) => rowAiKey(u, workingSetGroups)));
      next = next.map((row) => keys.has(rowAiKey(row, workingSetGroups))
        ? { ...row, min_order_qty: r.moq_value, ...(r.moq_unit ? { min_order_unit: r.moq_unit } : {}) }
        : row);
    } else if (op.type === 'sort_location') {
      const locKey = normalizeLocationKey(r.location);
      const order = new Map();
      (r.sorted || []).forEach((s, idx) => {
        order.set(rowAiKey(s, workingSetGroups), idx);
      });
      const inLoc = [];
      const outLoc = [];
      next.forEach((row) => {
        if (normalizeLocationKey(row.location) === locKey) inLoc.push(row);
        else outLoc.push(row);
      });
      inLoc.sort((a, b) => (order.get(rowAiKey(a, workingSetGroups)) ?? 1e9) - (order.get(rowAiKey(b, workingSetGroups)) ?? 1e9));
      next = [...outLoc, ...inLoc];
    }
  });
  return next.map((row, index) => ({ ...row, count_position: index + 1 }));
};

// Regroup a flat row list back into the { location: items[] } structure
// that the editor renders. Preserves per-row metadata (count_item_id,
// item_snapshot, etc.) by merging _original back in when present.
export const regroupFromRows = (rows = []) => {
  const grouped = {};
  rows.forEach((row) => {
    const loc = row.location || 'Individual Items';
    if (!grouped[loc]) grouped[loc] = [];
    const base = row._original || {};
    grouped[loc].push({
      ...base,
      inventory_item_id: row.inventory_item_id || row.id,
      id: row.inventory_item_id || row.id,
      count_item_id: row.count_item_id || null,
      description: row.description || base.description,
      name: row.name || base.name,
      category: row.category || base.category,
      vendor: row.vendor || base.vendor,
      brand: row.brand || base.brand,
      location: loc,
      par_level: row.par_level ?? null,
      par_defaulted: row.par_defaulted === true,
      par_unit: row.par_unit || base.par_unit || 'each',
      min_order_qty: row.min_order_qty ?? null,
      min_order_unit: row.min_order_unit || base.min_order_unit || 'each',
      unit: row.unit || base.unit || 'each',
      item_snapshot: {
        ...(base.item_snapshot || {
          id: row.inventory_item_id || row.id,
          name: row.name || row.description,
          description: row.description || row.name,
          brand: row.brand,
          category: row.category,
          vendor: row.vendor,
          unit: row.unit,
        }),
        par_defaulted: row.par_defaulted === true,
      },
    });
  });
  return buildReindexedTemplateItems(Object.entries(grouped));
};

// Add-all-items-in-location control rendered inside each folder header.
// Mirrors the per-item picker on ItemCard but operates over every item in the
// location, with a confirmation step before the bulk copy fires.
const LocationAddAllControl = ({
  location,
  itemCount,
  availableLocations = [],
  storeLocations: allStoreLocations = [],
  onCreateLocation,
  onAddAllToLocation,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [newLocationInput, setNewLocationInput] = useState('');
  const [hint, setHint] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0, width: 0 });

  const sourceKey = location.trim().toLowerCase();
  const filteredAvailableLocations = (availableLocations || []).filter((locName) => (
    typeof locName === 'string'
    && locName.trim()
    && locName.trim().toLowerCase() !== sourceKey
  ));

  const closePicker = () => {
    setShowPicker(false);
    setNewLocationInput('');
    setHint('');
  };

  // Anchor the floating picker beneath the trigger button. Falls back to a
  // centered position if the button rect is somehow unavailable.
  useEffect(() => {
    if (!showPicker) return undefined;

    const updatePos = () => {
      const btn = buttonRef.current;
      const vw = window.innerWidth;
      const margin = 8;
      const desiredWidth = Math.min(560, vw - margin * 2);
      if (!btn) {
        setPickerPos({
          top: margin,
          left: Math.max(margin, (vw - desiredWidth) / 2),
          width: desiredWidth,
        });
        return;
      }
      const rect = btn.getBoundingClientRect();
      // Right-align under the button when possible, but clamp to viewport.
      let left = rect.right - desiredWidth;
      if (left < margin) left = margin;
      if (left + desiredWidth > vw - margin) left = vw - margin - desiredWidth;
      setPickerPos({
        top: rect.bottom + 4,
        left,
        width: desiredWidth,
      });
    };

    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [showPicker]);

  // Close on outside click / Escape so the floating picker behaves like a popover.
  useEffect(() => {
    if (!showPicker) return undefined;
    const onDocClick = (e) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target)
        && buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        closePicker();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closePicker();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showPicker]);

  const chooseTarget = async (candidate) => {
    const trimmed = String(candidate || '').trim();
    if (!trimmed) return;
    if (trimmed.toLowerCase() === sourceKey) {
      setHint('Pick a different location.');
      return;
    }
    setHint('');
    const allKnown = [...new Set([...(allStoreLocations || []), ...(availableLocations || [])])];
    const existing = allKnown.find((l) => l.trim().toLowerCase() === trimmed.toLowerCase());
    const resolvedName = existing || trimmed;
    if (!existing && onCreateLocation) {
      await onCreateLocation(resolvedName);
    }
    closePicker();
    setConfirmTarget(resolvedName);
  };

  const onConfirm = async () => {
    if (!confirmTarget || submitting) return;
    setSubmitting(true);
    try {
      await onAddAllToLocation?.(location, confirmTarget);
    } finally {
      setSubmitting(false);
      setConfirmTarget(null);
    }
  };

  return (
    <div
      style={{ position: 'relative' }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-light btn-sm"
        title={`Copy all items in ${location} to another location`}
        aria-label={`Add all items in ${location} to another location`}
        onClick={(e) => {
          e.stopPropagation();
          setShowPicker((prev) => !prev);
        }}
        style={{
          height: '22px',
          padding: '0 6px',
          border: '1px solid #b8a79a',
          color: '#5a4a42',
          fontSize: '0.6rem',
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        <span className="d-none d-sm-inline">+ all new location</span>
        <span className="d-sm-none">+ all new loc</span>
      </button>

      {showPicker && (
        <div
          ref={pickerRef}
          className="border rounded bg-white shadow"
          style={{
            position: 'fixed',
            top: `${pickerPos.top}px`,
            left: `${pickerPos.left}px`,
            width: `${pickerPos.width}px`,
            maxHeight: 'calc(100vh - 24px)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9000,
            padding: '10px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#5a4a42',
              marginBottom: '8px',
            }}
          >
            Copy items in <span style={{ color: '#3a2f29' }}>{location}</span> to another room
          </div>
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              marginBottom: '8px',
              paddingRight: '2px',
            }}
          >
            {filteredAvailableLocations.length === 0 ? (
              <small className="text-muted" style={{ fontSize: '0.75rem' }}>No other locations yet.</small>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '6px',
                }}
              >
                {filteredAvailableLocations.map((locName) => (
                  <button
                    key={locName}
                    type="button"
                    className="btn btn-sm text-start"
                    style={{
                      fontSize: '0.75rem',
                      padding: '6px 8px',
                      border: '1px solid #d4c4b0',
                      color: '#5a4a42',
                      backgroundColor: '#faf8f6',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.2,
                    }}
                    onClick={() => chooseTarget(locName)}
                  >
                    {locName}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid #e8ddd4', paddingTop: '8px' }}>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="New location..."
                value={newLocationInput}
                onChange={(e) => { setNewLocationInput(e.target.value); setHint(''); }}
                style={{ fontSize: '0.8rem', padding: '4px 8px', height: '30px', flex: '1 1 auto' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    chooseTarget(newLocationInput);
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-primary"
                style={{ fontSize: '0.78rem', padding: '4px 12px', height: '30px', flexShrink: 0 }}
                onClick={() => chooseTarget(newLocationInput)}
              >
                Add
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: '0.78rem', padding: '4px 12px', height: '30px', flexShrink: 0 }}
                onClick={closePicker}
              >
                Cancel
              </button>
            </div>
            {hint && (
              <div style={{ fontSize: '0.7rem', color: '#b94a48', marginTop: '4px' }}>{hint}</div>
            )}
          </div>
        </div>
      )}

      {confirmTarget && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!submitting) setConfirmTarget(null);
          }}
        >
          <div
            className="bg-white rounded shadow"
            style={{ maxWidth: '90vw', width: '360px', padding: '16px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '8px' }}>
              Copy all items?
            </div>
            <div style={{ fontSize: '0.85rem', color: '#495057', marginBottom: '14px' }}>
              Add all {itemCount} {itemCount === 1 ? 'item' : 'items'} in
              {' '}<strong>{location}</strong> to <strong>{confirmTarget}</strong>?
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={submitting}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmTarget(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={submitting}
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm();
                }}
              >
                {submitting ? 'Adding…' : 'Yes, add all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Item Card Component
/**
 * Template-editor item card layout.
 *
 * Three real grid columns — gutter | body | PAR — replacing the previous
 * absolutely-positioned gutter and floating "Add Loc." pill. Absolute overlays
 * are why the remove button and the location pill ended up sitting on the item
 * name: nothing in the flow knew they were there. In a grid every element owns
 * a cell, so a long name shortens instead of running underneath a control.
 *
 * The meta rows share ONE label column, so Loc / Vendor / Cat / Cost all line
 * up regardless of label length.
 */
const TEMPLATE_ITEM_CARD_CSS = `
.tpl-group-order {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
.tpl-group-order-label {
  margin-right: 2px;
  color: #8B7B73;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.045em;
  text-transform: uppercase;
  white-space: nowrap;
}
.tpl-group-order-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 27px;
  margin: 0;
  padding: 2px 8px;
  border: 1px solid #D4C4B0;
  border-radius: 999px;
  background: #fff;
  color: #5A4A42;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.1;
  cursor: pointer;
}
.tpl-group-order-pill:hover,
.tpl-group-order-pill:focus-visible {
  border-color: #B79D84;
  background: #F6F1EC;
}
.tpl-group-order-pill[aria-pressed="true"] {
  border-color: #C8B5A0;
  background: #C8B5A0;
  color: #fff;
  box-shadow: 0 2px 7px rgba(90, 74, 66, 0.16);
}
.tpl-group-order-pill i { margin: 0 !important; }
.tpl-item-card {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 152px;
  align-items: stretch;
  gap: 0;
  min-height: 64px;
  overflow: visible;
}
/* Drag handle, position number and remove sit as one tight cluster at the top
   of the gutter — a control stranded at the bottom of a variable-height card
   reads as belonging to whatever text happens to be next to it. */
.tpl-item-gutter {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 2px 6px;
  border-right: 1px solid #F1EAE2;
  user-select: none;
}
.tpl-item-position {
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.4);
  line-height: 1;
}
.tpl-item-remove {
  width: 19px;
  height: 19px;
  padding: 0;
  margin-top: 1px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid #dc3545;
  color: #dc3545;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.tpl-item-remove:hover,
.tpl-item-remove:focus-visible {
  background: #dc3545;
  color: #fff;
}
.tpl-item-body {
  min-width: 0;
  padding: 8px 10px 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.tpl-item-title-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}
.tpl-item-name {
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--bs-emphasis-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tpl-item-locwrap { position: relative; flex-shrink: 0; }
.tpl-item-addloc {
  padding: 2px 7px;
  border: 1px solid #b8a79a;
  border-radius: 10px;
  background: #faf8f6;
  color: #5a4a42;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.3;
  cursor: pointer;
  white-space: nowrap;
  min-height: 22px;
}
.tpl-item-addloc:hover,
.tpl-item-addloc:focus-visible { background: #f1e9e2; }
.tpl-item-locpicker {
  position: absolute;
  top: 26px;
  right: 0;
  width: 190px;
  z-index: 30;
  padding: 6px;
}
/* max-content on the label column means the labels size themselves: rename
   one and the grid still lines up, with no magic pixel width to keep in sync. */
.tpl-item-meta {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 10px;
  row-gap: 2px;
  margin: 0;
  align-items: baseline;
}
.tpl-item-meta dt {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8B7B73;
  margin: 0;
}
.tpl-item-meta dd {
  margin: 0;
  min-width: 0;
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
/* The vendor cell hosts a popover, so it must not clip. The ellipsis lives on
   the trigger inside it instead of on the cell. */
.tpl-item-meta dd.tpl-item-vendor-cell {
  overflow: visible;
  position: relative;
}
.tpl-item-vendorcount {
  font-size: 0.6rem;
  font-weight: 700;
  color: #8B7B73;
  background: #F5F0EB;
  border: 1px solid #D9CEC2;
  border-radius: 999px;
  padding: 0 6px;
  white-space: nowrap;
  flex-shrink: 0;
}
.tpl-item-price {
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.76rem;
  font-weight: 600;
  color: #2E7D32;
}
.tpl-item-price small { color: #8B7B73; font-weight: 500; }
.tpl-item-parcol {
  display: flex;
  flex-direction: column;
  /* Left-aligned since the PAR control became a value + unit pair: ragged-right
     rows of different widths read as misaligned, ragged-left ones do not. */
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 6px 6px 6px 8px;
  border-left: 2px solid var(--bs-border-color);
  min-width: 0;
}
/* Phones: the PAR column drops under the body rather than squeezing the
   name into a few characters. */
@media (max-width: 480px) {
  .tpl-item-card { grid-template-columns: 30px minmax(0, 1fr); }
  .tpl-item-parcol {
    grid-column: 2;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 6px 10px;
    border-left: none;
    border-top: 1px solid #F1EAE2;
    padding: 6px 10px;
  }
}
`;

const ItemCard = ({
  item,
  location,
  // False on the category/vendor axes, where reordering has nothing to persist
  // to — the grab affordance would promise something the card can't do.
  draggable = true,
  onUpdatePar,
  onRemove,
  onAddToLocation,
  onCreateLocation,
  availableLocations = [],
  storeLocations: allStoreLocations = [],
  groupedItems = {},
  index = 0,
  orderCalculationsEnabled = false,
  resolveVendor,
  purchaseOptions = [],
  selectedOptionId = null,
  vendorNameById = {},
  onSelectVendor,
  ladderRungs = [],
  // Members in this item's working-set relationship group (itself included).
  // Under link-don't-merge the other vendors' products live on sibling
  // items, so group membership—not this item's own option count—is what
  // makes the row multi-vendor (the dead-signal rekey).
  groupMemberCount = 0
}) => {
  const snap = item.item_snapshot || {};
  const desc = snap.name || snap.description || item.name || item.description || 'Unnamed Item';
  const rawVendor = snap.vendor || item.vendor || '';
  const vendor = (typeof resolveVendor === 'function' ? resolveVendor(rawVendor) : rawVendor) || 'No vendor';
  // Unit price with the item's actual unit (e.g. "$38.50/btl").
  // Falls back across the various field names used across the ingest paths.
  const rawUnit = String(snap.unit || item.unit || 'ea').trim() || 'ea';
  const rawUnitPrice = item.unit_price ?? item.each_price ?? snap.unit_price ?? snap.each_price;
  const unitPriceNum = Number(rawUnitPrice);

  // Local state for input values to make them controlled components.
  const [parValue, setParValue] = useState(item.par_level ?? '');

  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [newLocationInput, setNewLocationInput] = useState('');
  const [newLocationHint, setNewLocationHint] = useState('');

  // Cell-editing isolation (best practice for editing cells in a long list):
  // keystrokes only touch this cell's local state — they never rebuild the
  // parent list or hit the network. The value is committed to the parent
  // (which persists it) one cell at a time, on blur or Enter. While a cell is
  // being edited we also block the prop→local sync below, so a background
  // refresh (e.g. another cell's save echoing back through
  // reloadOpenTemplateEditorItems) can't wipe what the user is typing.
  const parEditingRef = useRef(false);
  const lastCommittedParRef = useRef(item.par_level ?? '');

  // Sync local input from props only when this cell is NOT being edited.
  useEffect(() => {
    if (parEditingRef.current) return;
    const next = item.par_level ?? '';
    lastCommittedParRef.current = next;
    setParValue(next);
  }, [item.par_level]);

  // Push this cell's value up exactly once per change (no-op if unchanged), so
  // each cell saves independently and a fast typist isn't fighting other cells'
  // saves.
  const commitPar = (rawValue) => {
    const value = rawValue == null ? '' : String(rawValue);
    if (value === String(lastCommittedParRef.current ?? '')) return;
    lastCommittedParRef.current = value;
    onUpdatePar && onUpdatePar(item.id, location, value, item.par_unit || 'each');
  };

  /**
   * A PAR set in a vendor package: "2 tubs" is typed, 1,280 fl oz is stored.
   *
   * Both halves travel — the display pair so the PAR reads back the way it was
   * entered, and the normalized quantity so changing the preferred vendor can
   * never move it. The unit argument stays the fourth parameter it always was,
   * so a caller that ignores the fifth keeps working exactly as before.
   */
  const handleParCommit = ({ value, unit, optionId, normalized }) => {
    setParValue(value);
    if (value === String(lastCommittedParRef.current ?? '') && optionId === (item.par_purchase_option_id ?? null)) {
      return;
    }
    lastCommittedParRef.current = value;
    onUpdatePar && onUpdatePar(item.id, location, value, unit || item.par_unit || 'each', {
      purchaseOptionId: optionId ?? null,
      normalizedQuantity: normalized,
      normalizedUnit: rawUnit,
      displayQuantity: value === '' ? null : Number(value),
      displayUnit: unit || null,
    });
  };

  /* ── Count format (template default) ─────────────────────────────────── */
  // The item/template/location count-format default: an explicit pin here
  // wins over everything a count could infer, and the follow-latest toggle is
  // the item-scoped switch — no settings page. Local override echoes the
  // write instantly; the row's own columns catch up on the next reload.
  const [formatOverride, setFormatOverride] = useState(null);
  const [parPrompt, setParPrompt] = useState(null);

  const formatPresentation = useMemo(() => {
    const row = { ...item, ...(formatOverride || {}) };
    const ladder = ladderRungs && ladderRungs.length > 0
      ? { rungs: ladderRungs, unresolvedOptions: [] }
      : fallbackCountLadder({ countUnit: rawUnit, options: purchaseOptions });
    return resolveCountPresentation({
      row,
      options: purchaseOptions,
      rungs: ladder.rungs || [],
      unresolved: ladder.unresolvedOptions || [],
    });
  }, [item, formatOverride, ladderRungs, purchaseOptions, rawUnit]);

  const persistFormatPatch = async (patch) => {
    try {
      if (item.template_item_id != null) {
        await setCountFormatForTemplateItems([item.template_item_id], patch);
      }
      if (item.count_item_id != null) {
        await setCountFormatForLines([item.count_item_id], patch);
      } else if (item.count_session_id != null && item.id != null) {
        // The editor's rows are the template session's count_items.
        await setCountFormatForLines([item.id], patch);
      }
    } catch (err) {
      console.warn('[count-format] template pin failed:', err?.message || err);
    }
  };

  // Trigger A: an explicit count-format change is a natural moment to ask
  // about the PAR — immediately, with the exact consequence stated. Never
  // re-asks a transition this store already resolved; the suggestion is
  // cached, so the same transition never pays for two AI calls.
  const maybePromptParTransition = async (choice) => {
    try {
      const par = resolveRowPar(item, rawUnit);
      if (!par || par.normalized == null) return;
      const detection = detectParTransition({
        normalizedPar: par.normalized,
        parDisplay: par.display,
        parQtyPerPackage: par.snapshot?.quantity_per_package ?? null,
        newOptionId: choice?.optionId ?? null,
        newQtyPerPackage: choice?.qtyInCountUnit ?? null,
        newPackageLabel: choice?.label || choice?.unitLabel || null,
        oldPackageLabel: par.display?.unit ?? null,
        countUnit: rawUnit,
      });
      if (detection.autoResolve && detection.transition) {
        // Exact whole-package equivalent: nothing to ask. Refresh the friendly
        // display through the same commit path a hand-typed package PAR takes
        // (normalized quantity unchanged) and record the resolution.
        const t = detection.transition;
        handleParCommit({
          value: String(Math.round(t.newEquivalentPackages)),
          unit: t.newPackageLabel,
          optionId: t.newOptionId,
          normalized: t.normalizedPar,
        });
        const autoStoreId = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
        if (Number.isFinite(autoStoreId)) {
          await applyExactEquivalentRefresh({
            storeId: autoStoreId,
            inventoryItemId: item.inventory_item_id,
            transition: t,
          });
        }
        return;
      }
      if (!detection.required || !detection.transition) return;
      const storeId = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
      if (!Number.isFinite(storeId)) return;
      const stored = await fetchParTransitions(storeId, [item.inventory_item_id]);
      const row = stored[parTransitionKey(
        item.inventory_item_id, detection.transition.oldOptionId, detection.transition.newOptionId,
      )] || null;
      if (row?.decision && row.decision !== 'dismissed') return;
      const recommendation = await getParRecommendation({
        storeId,
        inventoryItemId: item.inventory_item_id,
        transition: detection.transition,
        cachedRow: row,
      });
      setParPrompt({ transition: detection.transition, recommendation, busy: false });
    } catch (err) {
      console.warn('[par-transition] editor check skipped:', err?.message || err);
    }
  };

  const handleFormatSelect = async (patch, choice) => {
    setFormatOverride({
      count_display_purchase_option_id: patch.count_display_purchase_option_id ?? null,
      count_display_unit: patch.count_display_unit ?? null,
      follow_latest_purchase: patch.follow_latest_purchase !== false,
    });
    await persistFormatPatch(patch);
    await maybePromptParTransition(choice);
  };

  const handleFollowLatestToggle = async (next) => {
    const patch = { follow_latest_purchase: next !== false };
    setFormatOverride((prev) => ({ ...(prev || {}), ...patch }));
    await persistFormatPatch(patch);
  };

  const settleEditorParTransition = async (decision) => {
    const prompt = parPrompt;
    if (!prompt) return;
    setParPrompt({ ...prompt, busy: true });
    try {
      if (decision === 'preserve_package_count') {
        // The same commit path a hand-typed package PAR takes — display pair
        // plus normalized quantity, persisted and propagated by the parent.
        handleParCommit({
          value: String(prompt.transition.preservePackages),
          unit: prompt.transition.newPackageLabel,
          optionId: prompt.transition.newOptionId,
          normalized: prompt.transition.preserveNormalizedPar,
        });
      }
      const storeId = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
      if (Number.isFinite(storeId)) {
        await recordParTransition({
          storeId,
          inventoryItemId: item.inventory_item_id,
          transition: prompt.transition,
          recommendation: prompt.recommendation,
          decision,
          resultingNormalizedPar: decision === 'preserve_package_count'
            ? prompt.transition.preserveNormalizedPar
            : prompt.transition.normalizedPar,
        });
      }
      setParPrompt(null);
    } catch (err) {
      console.warn('[par-transition] editor decision failed:', err?.message || err);
      setParPrompt({ ...prompt, busy: false });
    }
  };

  // Compute every location bucket this item already occupies so we can hide
  // all of them from the picker (not just the current bucket).
  const itemKey = item.inventory_item_id || item.id;
  const itemAlreadyInLocations = useMemo(() => {
    const occupied = new Set();
    Object.entries(groupedItems || {}).forEach(([loc, items]) => {
      if ((items || []).some(i => (i.inventory_item_id || i.id) === itemKey)) {
        occupied.add(loc.trim().toLowerCase());
      }
    });
    return occupied;
  }, [groupedItems, itemKey]);

  const filteredAvailableLocations = (availableLocations || []).filter((locName) => (
    typeof locName === 'string'
    && locName.trim()
    && !itemAlreadyInLocations.has(locName.trim().toLowerCase())
  ));

  const submitAddToLocation = async (candidateLocation) => {
    const trimmed = String(candidateLocation || '').trim();
    if (!trimmed) return;
    // Reject if the item is already in this location
    if (itemAlreadyInLocations.has(trimmed.toLowerCase())) {
      setNewLocationHint('Item is already in this location.');
      return;
    }
    setNewLocationHint('');
    // Case-insensitive dedup against all known store locations
    const allKnown = [...new Set([...(allStoreLocations || []), ...(availableLocations || [])])];
    const existing = allKnown.find(l => l.trim().toLowerCase() === trimmed.toLowerCase());
    const resolvedName = existing || trimmed;
    // If brand-new location, persist to store_details and update parent state
    if (!existing && onCreateLocation) {
      await onCreateLocation(resolvedName);
    }
    onAddToLocation?.(item, location, resolvedName);
    setNewLocationInput('');
    setShowLocationPicker(false);
  };

  const category = snap.category || item.category || '';

  // A canonical item has several ways to buy it. The vendor row becomes a
  // picker so the template can carry the line's DEFAULT vendor; a
  // single-vendor item keeps the plain name.
  const linkage = canonicalLinkage(purchaseOptions, { groupMemberCount });

  // One item bought four ways has no single price, so the Cost cell shows the
  // spread rather than whichever vendor last wrote the legacy column. The cell
  // already appends "/unit", so a range that is per-package carries its own
  // unit and must not get a second one.
  const costCell = linkage.isCanonical
    ? costCellForSummary(purchaseOptionsSummary(purchaseOptions), rawUnit)
    : null;
  const priceDisplay = costCell
    ? costCell.text
    : Number.isFinite(unitPriceNum) && unitPriceNum > 0
      ? `$${unitPriceNum.toFixed(2)}`
      : '';
  const priceCarriesUnit = !!costCell;

  // The units this item's PAR may be expressed in. Built from the same ladder
  // the count sheet uses, so a PAR in cases and a count in cases can never
  // disagree about how big a case is. Empty when the ladder has not loaded —
  // the control then offers the item's own unit, which is today's behaviour.
  const parRungs = ladderRungs && ladderRungs.length > 0 ? ladderRungs : [];

  return (
    <div
      className="tpl-item-card mb-2 bg-white border rounded"
      style={{ cursor: draggable ? 'grab' : 'default' }}
    >
      {/* Gutter: drag handle, position, remove. A real grid column, so nothing
          here can ever sit on top of the item name. */}
      <div className="tpl-item-gutter">
        {draggable && (
          <i
            className="feather icon-menu"
            title="Drag to reorder"
            aria-hidden="true"
            style={{ color: 'rgba(100, 80, 60, 0.55)', fontSize: '0.95rem', lineHeight: 1 }}
          />
        )}
        <span className="tpl-item-position">{(item.count_position || index + 1)}</span>
        <button
          type="button"
          className="tpl-item-remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove && onRemove(item, location);
          }}
          title="Remove from count"
          aria-label={`Remove ${desc} from count`}
        >
          &times;
        </button>
      </div>

      <div className="tpl-item-body">
        {/* Name + the one action that belongs beside it. The action sits IN the
            row rather than floating over it, so a long name shortens instead of
            running underneath a button. */}
        <div className="tpl-item-title-row">
          <span className="tpl-item-name" title={desc}>{desc}</span>
          <div className="tpl-item-locwrap">
            <button
              type="button"
              className="tpl-item-addloc"
              onClick={(e) => {
                e.stopPropagation();
                setShowLocationPicker((prev) => !prev);
              }}
              title="Copy item to another location"
              aria-expanded={showLocationPicker}
              aria-label={`Copy ${desc} to another location`}
            >
              + Loc
            </button>

            {showLocationPicker && (
              <div
                className="tpl-item-locpicker border rounded bg-white shadow-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ maxHeight: '120px', overflowY: 'auto', marginBottom: '6px' }}>
                  {filteredAvailableLocations.length === 0 ? (
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>No other locations yet.</small>
                  ) : (
                    filteredAvailableLocations.map((locName) => (
                      <button
                        key={locName}
                        type="button"
                        className="btn btn-sm w-100 text-start mb-1"
                        style={{ fontSize: '0.7rem', padding: '2px 6px', border: '1px solid #d4c4b0', color: '#5a4a42', backgroundColor: '#faf8f6' }}
                        onClick={() => submitAddToLocation(locName)}
                      >
                        {locName}
                      </button>
                    ))
                  )}
                </div>
                <div style={{ borderTop: '1px solid #e8ddd4', paddingTop: '5px' }}>
                  <div className="d-flex gap-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="New location..."
                      value={newLocationInput}
                      onChange={(e) => { setNewLocationInput(e.target.value); setNewLocationHint(''); }}
                      style={{ fontSize: '0.7rem', padding: '2px 5px', height: '24px' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          submitAddToLocation(newLocationInput);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      style={{ fontSize: '0.68rem', padding: '2px 6px', height: '24px', flexShrink: 0 }}
                      onClick={() => submitAddToLocation(newLocationInput)}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      style={{ fontSize: '0.68rem', padding: '2px 6px', height: '24px', flexShrink: 0 }}
                      onClick={() => { setShowLocationPicker(false); setNewLocationInput(''); setNewLocationHint(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                  {newLocationHint && (
                    <div style={{ fontSize: '0.65rem', color: '#b94a48', marginTop: '3px' }}>{newLocationHint}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Meta grid: one label column, one value column. Every row lines up
            because they share the grid, not because each row guesses the same
            min-width. */}
        <dl className="tpl-item-meta">
          {location && (
            <>
              <dt>Loc</dt>
              <dd title={location}>{location}</dd>
            </>
          )}

          <dt>Vendor</dt>
          <dd className={linkage.isCanonical ? 'tpl-item-vendor-cell' : undefined}>
            {linkage.isCanonical ? (
              <VendorSelectPopover
                options={purchaseOptions}
                countUnit={rawUnit}
                selectedOptionId={selectedOptionId}
                vendorNameById={vendorNameById}
                itemName={desc}
                compact
                // The template editor is a browsing surface, not a keypad, so
                // hovering a vendor is a safe way to see who supplies an item
                // without committing to opening anything.
                openOnHover
                onSelect={onSelectVendor ? (optionId) => onSelectVendor(item, optionId) : undefined}
              />
            ) : (
              <span title={vendor}>{vendor}</span>
            )}
            {/* The count badge only earns its place when it says something the
                trigger does not. A multi-vendor item's trigger already reads
                "Multi-vendor", so repeating it beside itself was noise — but
                "3 linked products" (one vendor, several packs) is a different
                fact and still worth showing. */}
            {linkage.isCanonical && !linkage.multiVendor && (
              <span className="tpl-item-vendorcount">{linkage.badgeLabel}</span>
            )}
          </dd>

          {category && (
            <>
              <dt>Cat</dt>
              <dd title={category}>{category}</dd>
            </>
          )}

          {priceDisplay && (
            <>
              <dt>Cost</dt>
              <dd>
                <span className="tpl-item-price">
                  {priceDisplay}
                  {!priceCarriesUnit && <small>/{rawUnit}</small>}
                </span>
              </dd>
            </>
          )}
        </dl>
      </div>

        {/* PAR — the card's third grid column, so it keeps its own
            width instead of competing with the item name for space. */}
        <div
          className="tpl-item-parcol"
          style={{
            backgroundColor: orderCalculationsEnabled
              ? 'rgba(248, 249, 250, 0.8)'
              : 'rgba(248, 249, 250, 0.3)',
            cursor: orderCalculationsEnabled ? 'default' : 'move',
            pointerEvents: orderCalculationsEnabled ? 'auto' : 'none',
            opacity: orderCalculationsEnabled ? 1 : 0.6
          }}
        >
          {/* PAR — value and unit, both readable and both editable. The old
              control printed the unit as a watermark behind the number, so a
              PAR read as bare "10" and could only ever be set in the item's
              own unit. You think "keep two tubs on hand", not "keep 1,280
              fluid ounces"; this lets you say the first and stores the
              second. */}
          <ParUnitControl
            value={parValue}
            unit={item.par_display_unit || item.par_unit}
            optionId={item.par_purchase_option_id ?? null}
            countUnit={rawUnit}
            rungs={parRungs}
            disabled={!orderCalculationsEnabled}
            compact
            onCommit={handleParCommit}
          />

          {/* The MOQ pill and its "varies by vendor" caption used to sit here.
              Both are gone. A minimum order quantity belongs to a VENDOR
              PRODUCT, not to the item: one number on an item card was a lie the
              moment the item had two suppliers, which is exactly why the card
              had grown a caption admitting it. Minimums are set on the vendor's
              purchase option (purchase_options.moq_packages) and applied in
              Savings Review, where a vendor and a package have been chosen. */}

          {/* Count format — the template default for this line. Explicit
              selection pins it (follow-latest turns off in the same write);
              the toggle is the per-line follow switch. Renders as passive
              context when there is only one countable package. */}
          <div style={{ marginTop: 4 }}>
            <CountFormatControl
              presentation={formatPresentation}
              countUnit={rawUnit}
              compact
              onSelectFormat={handleFormatSelect}
              onToggleFollowLatest={handleFollowLatestToggle}
            />
          </div>
        </div>
        {parPrompt && (
          <ParTransitionPrompt
            itemName={desc}
            transition={parPrompt.transition}
            recommendation={parPrompt.recommendation}
            busy={parPrompt.busy}
            onKeep={() => settleEditorParTransition('keep_quantity')}
            onPreservePackageCount={() => settleEditorParTransition('preserve_package_count')}
            onDismiss={() => settleEditorParTransition('dismissed')}
          />
        )}
    </div>
  );
};

/**
 * The columns that record a PAR entered in a vendor package.
 *
 * Two halves, and both matter. The display pair is what the user typed, kept
 * so the PAR reads back the way it was set. The normalized quantity is what
 * on-hand is compared against, and storing it is what guarantees that making a
 * different vendor preferred cannot move a PAR — re-deriving it on read would
 * turn "2 Costco tubs" into "2 of whoever is cheapest this week".
 *
 * Returns {} when there is nothing to record, so a PAR typed in the item's own
 * unit writes exactly the columns it always did.
 */
const parNormalizationPatch = (normalization) => {
  if (!normalization) return {};
  const { purchaseOptionId, normalizedQuantity, normalizedUnit, displayQuantity, displayUnit } = normalization;
  if (purchaseOptionId == null && normalizedQuantity == null) return {};
  return {
    par_purchase_option_id: purchaseOptionId ?? null,
    // null, never 0 — a PAR we could not convert is not a PAR of nothing.
    par_normalized_quantity: normalizedQuantity ?? null,
    par_normalized_unit: normalizedQuantity == null ? null : normalizedUnit ?? null,
    par_display_quantity: displayQuantity ?? null,
    par_display_unit: displayUnit ?? null,
  };
};

const normalizeGroupedItems = (templateItems = {}) => {
  const normalized = {};
  const locationEntries = Object.entries(templateItems || {}).filter(([, items]) => Array.isArray(items) && items.length > 0);
  locationEntries.forEach(([location, items], locationIndex) => {
    const sortedItems = [...items].sort((a, b) => {
      const aPos = Number.parseFloat(a?.count_position);
      const bPos = Number.parseFloat(b?.count_position);
      const hasAPos = Number.isFinite(aPos);
      const hasBPos = Number.isFinite(bPos);
      if (hasAPos && hasBPos && aPos !== bPos) return aPos - bPos;
      if (hasAPos && !hasBPos) return -1;
      if (!hasAPos && hasBPos) return 1;
      const aDesc = (a?.item_snapshot?.description || a?.description || a?.name || '').toLowerCase();
      const bDesc = (b?.item_snapshot?.description || b?.description || b?.name || '').toLowerCase();
      return aDesc.localeCompare(bDesc);
    });

    // Guard against stale duplicate DB rows: if two entries share the same
    // inventory_item_id inside the same location bucket, keep only the one
    // with the more specific count_item_id (or the first encountered).
    // This prevents React duplicate-key warnings and rendering glitches until
    // the DB rows are cleaned up by a proper save.
    const seenInvIds = new Set();
    const dedupedItems = sortedItems.filter((item) => {
      const invId = String(item.inventory_item_id || item.id || '');
      if (!invId || seenInvIds.has(invId)) return false;
      seenInvIds.add(invId);
      return true;
    });

    normalized[location] = dedupedItems.map((item, itemIndex) => ({
      ...item,
      location,
      location_position: locationIndex + 1,
      count_position: itemIndex + 1
    }));
  });
  return normalized;
};

const buildReindexedTemplateItems = (locationEntries = []) => {
  const next = {};
  locationEntries.forEach(([location, items], locationIndex) => {
    next[location] = (items || []).map((item, itemIndex) => ({
      ...item,
      location,
      location_position: locationIndex + 1,
      count_position: itemIndex + 1
    }));
  });
  return next;
};

/* ── Group-by axes ────────────────────────────────────────────────────────────
 *
 * The template's items live in a LOCATION-keyed object, and that is not a
 * display choice: the bucket key is written straight to count_items.location by
 * buildReindexedTemplateItems + persistTemplateItemsToSession, and the order
 * within it is the route a counter physically walks.
 *
 * Category and vendor are therefore DISPLAY axes only. They re-bucket the same
 * rows for reading and never become the stored key — everything below is pure,
 * and its output must never be assigned to groupedItems, handed to onUpdate, or
 * fed to buildReindexedTemplateItems. Letting a category name reach that chain
 * would rewrite every row's location on the next drag.
 *
 * Reordering is only meaningful along the axis that persists, so dragging stays
 * gated to Location mode (the same gate the search filter already uses).
 */
const GROUP_MODES = [
  { key: 'category', label: 'Category', icon: 'icon-tag', glyph: '🏷️' },
  { key: 'vendor', label: 'Vendor', icon: 'icon-truck', glyph: '🚚' },
  { key: 'location', label: 'Location', icon: 'icon-map-pin', glyph: '📁' }
];

export const DEFAULT_GROUP_MODE = 'location';

const GROUP_FALLBACK_LABEL = {
  category: 'Uncategorized',
  vendor: 'No vendor',
  location: 'No Location'
};

// Which bucket an item belongs to on a given axis. Vendor goes through the
// vendor directory so aliases fold into their canonical name instead of
// splitting one supplier across several folders.
export const groupValueForItem = (item, mode, resolveVendor) => {
  const snapshot = item?.item_snapshot || {};
  if (mode === 'vendor') {
    const raw = snapshot.vendor || item?.vendor || '';
    const resolved = typeof resolveVendor === 'function' ? resolveVendor(raw) : raw;
    return String(resolved || '').trim() || GROUP_FALLBACK_LABEL.vendor;
  }
  if (mode === 'category') {
    return String(snapshot.category || item?.category || '').trim() || GROUP_FALLBACK_LABEL.category;
  }
  return String(item?.location || '').trim() || GROUP_FALLBACK_LABEL.location;
};

// Missing positions sort last rather than first, so a row that has never been
// placed doesn't jump ahead of the ordered ones.
const positionValue = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const itemDescription = (item) => String(
  item?.item_snapshot?.description || item?.description || item?.name || ''
);

/**
 * Re-bucket location-keyed entries onto the requested display axis.
 *
 * Location mode returns the input untouched — the identity case is what
 * guarantees this layer cannot regress the existing behaviour. The derived
 * modes keep the walk order inside each bucket (location, then position within
 * that location) so an item's neighbours stay recognisable after regrouping.
 */
export const buildDisplayGroups = (locationEntries = [], mode = DEFAULT_GROUP_MODE, resolveVendor) => {
  if (mode === 'location') return locationEntries;

  const byGroup = new Map();
  locationEntries.forEach(([, items]) => {
    (items || []).forEach((item) => {
      const key = groupValueForItem(item, mode, resolveVendor);
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(item);
    });
  });

  return [...byGroup.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, items]) => [
      key,
      items.sort((left, right) => (
        positionValue(left.location_position) - positionValue(right.location_position)
        || positionValue(left.count_position) - positionValue(right.count_position)
        || itemDescription(left).localeCompare(itemDescription(right))
      ))
    ]);
};

// Persist a shared per-item field (the PAR) to every count_items row in the
// session that matches the given inventory_item_id, so all sibling location
// rows see the same value. Without this, PAR edits made in the template
// editor only live in React state until the user clicks Save — and new counts
// created from the template read stale values.
//
// Also propagates the change up to the parent template (count_template_items
// for PAR) so that the
// NEXT count created from the same template pre-fills these values, instead
// of inheriting whatever PAR the template had at original setup time.
// The PAR-normalization columns arrive with migration 20260730120000, which may
// not be deployed everywhere yet. A write that includes them must not take the
// PAR itself down with it.
const PAR_NORMALIZATION_COLUMNS = [
  'par_purchase_option_id',
  'par_normalized_quantity',
  'par_normalized_unit',
  'par_display_quantity',
  'par_display_unit',
];

const isMissingColumnError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return ['PGRST204', 'PGRST205', '42703'].includes(code)
    || /column .* does not exist|schema cache/i.test(message);
};

const withoutParNormalization = (payload) => {
  const stripped = { ...payload };
  PAR_NORMALIZATION_COLUMNS.forEach((column) => { delete stripped[column]; });
  return stripped;
};

const persistSharedItemFieldToSession = async (sessionId, inventoryItemId, fields) => {
  if (!sessionId || !inventoryItemId) return null;
  const payload = { ...fields, updated_at: new Date().toISOString() };
  const write = (body) => supabase
    .from('count_items')
    .update(body)
    .eq('count_session_id', sessionId)
    .eq('inventory_item_id', inventoryItemId);

  let { error } = await write(payload);
  if (error && isMissingColumnError(error)
      && PAR_NORMALIZATION_COLUMNS.some((column) => column in payload)) {
    // Retry with just the columns that have always existed. The PAR is saved
    // either way; only the vendor-package provenance is lost, and the display
    // pair the user typed remains in par_level/par_unit.
    console.warn('[ItemLocationFolders] PAR normalization columns unavailable; saving the PAR alone.');
    ({ error } = await write(withoutParNormalization(payload)));
  }
  if (error) {
    console.error('❌ [ItemLocationFolders] Failed to persist shared field:', error);
  } else {
    // Tell the Counts overview what just changed. The editor and the overview
    // share one page (count.html only toggles which row is display:none), so
    // CountInterface stays mounted with a count_items cache it fetched at page
    // load — before these edits existed. Without this signal that cache never
    // learns about the write, and a template whose PARs were just filled in
    // keeps reading as "Needs PARs" (and opens the par-setup step blank) until
    // a hard refresh. Announced per successful write only, so a failed save
    // never shows up as set.
    try {
      document.dispatchEvent(new CustomEvent('countItemFieldsChanged', {
        detail: { sessionId, inventoryItemId, fields: { ...fields } },
      }));
    } catch (_) {}
  }
  try {
    const propagation = { sessionId, inventoryItemId };
    if ('par_level' in fields) propagation.par_level = fields.par_level;
    if ('par_unit' in fields) propagation.par_unit = fields.par_unit;
    if ('par_defaulted' in fields) propagation.par_defaulted = fields.par_defaulted === true;
    if ('min_order_qty' in fields) propagation.min_order_qty = fields.min_order_qty;
    if ('min_order_unit' in fields) propagation.min_order_unit = fields.min_order_unit;
    return await propagateGlobalCountFieldsToParents(propagation);
  } catch (err) {
    console.warn('[ItemLocationFolders] template propagation failed:', err);
    return null;
  }
};

const persistTemplateItemsToSession = async (sessionId, groupedItems) => {
  if (!sessionId) return;
  const updates = [];
  Object.entries(groupedItems).forEach(([location, items], locationIndex) => {
    (items || []).forEach((item, itemIndex) => {
      // count_item_id is the UUID of the row in count_items;
      // item.id is the inventory_item UUID and must NOT be used for the DB lookup.
      const countItemId = item.count_item_id || null;
      if (!countItemId) return; // skip items not yet persisted
      updates.push({
        id: countItemId,
        location,
        location_position: locationIndex + 1,
        count_position: itemIndex + 1
      });
    });
  });
  for (const update of updates) {
    const payload = countItemsSupportsLocationPosition
      ? {
        location: update.location,
        location_position: update.location_position,
        count_position: update.count_position,
        updated_at: new Date().toISOString()
      }
      : {
        location: update.location,
        count_position: update.count_position,
        updated_at: new Date().toISOString()
      };
    const { error } = await supabase
      .from('count_items')
      .update(payload)
      .eq('id', update.id)
      .eq('count_session_id', sessionId);
    if (error && countItemsSupportsLocationPosition && `${error.message || ''}`.includes('location_position')) {
      countItemsSupportsLocationPosition = false;
      await supabase
        .from('count_items')
        .update({
          location: update.location,
          count_position: update.count_position,
          updated_at: new Date().toISOString()
        })
        .eq('id', update.id)
        .eq('count_session_id', sessionId);
    } else if (error) {
      throw error;
    }
  }
  // Signal the Counts overview to reload and rescan icons for this template.
  // Passing sessionId in the detail lets the overview drop only this entry
  // from the icon cache rather than blanking all cards simultaneously.
  document.dispatchEvent(new CustomEvent('refreshCountInterface', { detail: { sessionId } }));
};

const ItemLocationFolders = ({
  templateItems = {},
  onUpdate,
  sessionId = null,
  orderCalculationsEnabled = false
}) => {
  const [collapsedFolders, setCollapsedFolders] = useState(new Set());
  const [groupedItems, setGroupedItems] = useState({});
  const [storeLocations, setStoreLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Display axis only — groupedItems stays location-keyed regardless.
  const [groupMode, setGroupMode] = useState(DEFAULT_GROUP_MODE);
  const [templateSyncToast, setTemplateSyncToast] = useState(null);
  const templateSyncToastTimerRef = useRef(null);

  const showTemplateSyncToast = (msg) => {
    if (!msg) return;
    setTemplateSyncToast(msg);
    clearTimeout(templateSyncToastTimerRef.current);
    templateSyncToastTimerRef.current = setTimeout(() => setTemplateSyncToast(null), 3000);
  };
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const isFilterActive = normalizedSearch.length > 0;
  const isLocationMode = groupMode === 'location';
  // Drag writes the bucket key back as count_items.location, so reordering is
  // only offered on the axis that key belongs to — and never while a search is
  // hiding rows the reindex would still renumber.
  const isReorderable = isLocationMode && !isFilterActive;

  const handleGroupModeChange = (nextMode) => {
    if (nextMode === groupMode) return;
    setGroupMode(nextMode);
    // Folder collapse state is keyed by bucket name, which changes meaning
    // entirely on a new axis. Start every axis fully expanded.
    setCollapsedFolders(new Set());
  };

  const storeId = useMemo(() => {
    const raw = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
    return Number.isFinite(raw) ? raw : null;
  }, []);

  // The store's working-set relationship groups, for group-aware AI-op
  // identity (rowAiKey) and the multi-vendor rekey on the item cards. Null on
  // any failure — everything then behaves item-scoped, exactly as before the
  // count-through.
  const [workingSetGroups, setWorkingSetGroups] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!storeId) return undefined;
    fetchWorkingSetGroupsForStore(storeId)
      .then((groups) => { if (!cancelled) setWorkingSetGroups(groups); })
      .catch(() => { if (!cancelled) setWorkingSetGroups(null); });
    return () => { cancelled = true; };
  }, [storeId]);

  const notifyAi = (message, type = 'success') => {
    if (type === 'error') {
      console.error(`[ItemLocationFolders AI] ${message}`);
      try { alert(message); } catch (_) {}
    } else {
      console.log(`[ItemLocationFolders AI] ${message}`);
    }
  };

  // Ref mirrors groupedItems so async callbacks (autoSaveItemToDB completion,
  // post-insert resyncs) can read the latest PAR values without relying
  // on stale closures from the state at the time the add was kicked off.
  const groupedItemsRef = useRef(groupedItems);

  const { resolveVendor } = useVendorDirectory();

  // Purchase options for every item on the template, keyed by
  // inventory_item_id, plus each line's chosen vendor product. The template
  // editor is where a line's DEFAULT vendor is set; the count tool can then
  // override it per session. Both loads are non-fatal — the card falls back to
  // the snapshot's single vendor name when purchasing data is unavailable.
  const [optionsByItemId, setOptionsByItemId] = useState({});
  // Group-pooled option lists: each grouped item's pool is the union of its
  // working-set siblings' options, so a card whose sibling holds the second
  // vendor still shows the real multi-vendor picture. Identity map when the
  // store has no groups.
  const pooledOptionsByItemId = useMemo(
    () => expandOptionsAcrossGroup(optionsByItemId, workingSetGroups),
    [optionsByItemId, workingSetGroups],
  );
  const [vendorNameById, setVendorNameById] = useState({});
  const [selectedOptionByCountItemId, setSelectedOptionByCountItemId] = useState({});
  // The package ladder per item — which vendor packages a PAR may be expressed
  // in, and how much one of each is worth in the item's count unit. Built by
  // the server so the template editor and the count tool can never disagree
  // about the size of a case. {} means the unit model is unavailable, and the
  // PAR control falls back to the item's own unit: today's behaviour.
  const [ladderRungsByItemId, setLadderRungsByItemId] = useState({});

  const inventoryIdKey = useMemo(() => (
    [...new Set(
      Object.values(groupedItems || {})
        .flat()
        .map((row) => row?.inventory_item_id ?? row?.item_snapshot?.id ?? null)
        .filter((id) => id != null)
        .map(String),
    )].sort().join(',')
  ), [groupedItems]);

  const countItemIdKey = useMemo(() => (
    [...new Set(
      Object.values(groupedItems || {})
        .flat()
        .map((row) => row?.count_item_id ?? null)
        .filter((id) => id != null)
        .map(String),
    )].sort().join(',')
  ), [groupedItems]);

  useEffect(() => {
    const ids = inventoryIdKey ? inventoryIdKey.split(',') : [];
    if (!storeId || ids.length === 0) {
      setOptionsByItemId({});
      return undefined;
    }
    let cancelled = false;
    fetchOptionsForItems(storeId, ids)
      .then((grouped) => { if (!cancelled) setOptionsByItemId(grouped || {}); })
      .catch((err) => {
        console.warn('[ItemLocationFolders] Purchase options unavailable (non-fatal):', err?.message);
        if (!cancelled) setOptionsByItemId({});
      });
    fetchCountLadders(storeId, ids)
      .then(({ laddersByItemId }) => {
        if (cancelled) return;
        const rungs = {};
        Object.entries(laddersByItemId || {}).forEach(([itemId, ladder]) => {
          rungs[itemId] = ladder.rungs || [];
        });
        setLadderRungsByItemId(rungs);
      })
      .catch((err) => {
        console.warn('[ItemLocationFolders] Package ladders unavailable (non-fatal):', err?.message);
        if (!cancelled) setLadderRungsByItemId({});
      });
    return () => { cancelled = true; };
  }, [storeId, inventoryIdKey]);

  useEffect(() => {
    const ids = countItemIdKey ? countItemIdKey.split(',') : [];
    if (ids.length === 0) {
      setSelectedOptionByCountItemId({});
      return undefined;
    }
    let cancelled = false;
    fetchSelectedOptionsForCountItems(ids)
      .then((byId) => { if (!cancelled) setSelectedOptionByCountItemId(byId || {}); })
      .catch(() => { if (!cancelled) setSelectedOptionByCountItemId({}); });
    return () => { cancelled = true; };
  }, [countItemIdKey]);

  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    Promise.resolve(supabase.from('vendors').select('id, display_name').eq('store_id', storeId))
      .then(({ data, error }) => {
        if (cancelled || error) return;
        const map = {};
        (data || []).forEach((vendor) => { if (vendor?.id != null) map[vendor.id] = vendor.display_name; });
        setVendorNameById(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [storeId]);

  // Setting the default vendor for a line applies to every location bucket
  // holding the same item: one item is bought once, however many rooms it is
  // counted in.
  const handleSelectVendor = useCallback(async (row, optionId) => {
    const inventoryId = row?.inventory_item_id ?? row?.item_snapshot?.id ?? null;
    const siblingIds = Object.values(groupedItemsRef.current || {})
      .flat()
      .filter((other) => (other?.inventory_item_id ?? other?.item_snapshot?.id ?? null) === inventoryId)
      .map((other) => other?.count_item_id)
      .filter((id) => id != null);
    const ids = siblingIds.length > 0
      ? siblingIds
      : (row?.count_item_id != null ? [row.count_item_id] : []);
    if (ids.length === 0) return;

    // Optimistic: the picker closes on the caller's await, so the label must
    // already be right when it does.
    setSelectedOptionByCountItemId((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = optionId || null; });
      return next;
    });
    try {
      await setSelectedPurchaseOptionForLines(ids, optionId);
    } catch (err) {
      console.warn('[ItemLocationFolders] Could not save the vendor choice:', err?.message);
      showTemplateSyncToast('Could not save that vendor choice.');
    }
  }, []);

  useEffect(() => {
    setGroupedItems(normalizeGroupedItems(templateItems));
  }, [templateItems]);

  useEffect(() => {
    groupedItemsRef.current = groupedItems;
  }, [groupedItems]);

  // Dismiss the full-screen "loading…" splash that count.html shows while a
  // large template is fetched into the editor, once the editor is actually
  // rendering items (or immediately if the template is genuinely empty — so the
  // splash never sticks). Guarded/optional global; a no-op outside that page.
  useEffect(() => {
    const hasItems = Object.keys(groupedItems || {}).length > 0;
    const templateEmpty = Object.keys(templateItems || {}).length === 0;
    if (!hasItems && !templateEmpty) return;
    try {
      if (typeof window !== 'undefined' && typeof window.__hideCountEditorLoading === 'function') {
        window.__hideCountEditorLoading();
      }
    } catch (_) { /* best-effort */ }
  }, [groupedItems, templateItems]);

  useEffect(() => {
    return () => { clearTimeout(templateSyncToastTimerRef.current); };
  }, []);

  useEffect(() => {
    const loadStoreLocations = async () => {
      try {
        const selectedStoreId = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
        if (!Number.isFinite(selectedStoreId)) return;

        const locationSet = new Set();

        const { data: detailsRows } = await supabase
          .from('store_details')
          .select('locations')
          .eq('store_id', selectedStoreId)
          .limit(1);
        const configuredLocations = detailsRows?.[0]?.locations;
        if (Array.isArray(configuredLocations)) {
          configuredLocations.forEach((loc) => {
            if (typeof loc === 'string' && loc.trim()) locationSet.add(loc.trim());
          });
        }

        const { data: inventoryRows } = await supabase
          .from('inventory_items')
          .select('location')
          .eq('store_id', selectedStoreId)
          .not('location', 'is', null)
          .limit(2000);
        (inventoryRows || []).forEach((row) => {
          if (typeof row?.location === 'string' && row.location.trim()) {
            locationSet.add(row.location.trim());
          }
        });

        setStoreLocations([...locationSet].sort((a, b) => a.localeCompare(b)));
      } catch (error) {
        console.warn('⚠️ [ItemLocationFolders] Failed to load store locations:', error?.message || error);
      }
    };

    loadStoreLocations();
  }, []);

  const visibleLocationEntries = useMemo(() => {
    if (!isFilterActive) {
      return Object.entries(groupedItems);
    }
    return Object.entries(groupedItems)
      .map(([location, items]) => {
        const filteredItems = (items || []).filter((item) => {
          const desc = (item.item_snapshot?.description || item.description || item.name || '').toLowerCase();
          const rawVendor = item.item_snapshot?.vendor || item.vendor || '';
          const vendor = resolveVendor(rawVendor).toLowerCase();
          const category = (item.item_snapshot?.category || item.category || '').toLowerCase();
          const locationText = (item.location || location || '').toLowerCase();
          return desc.includes(normalizedSearch)
            || vendor.includes(normalizedSearch)
            || category.includes(normalizedSearch)
            || locationText.includes(normalizedSearch);
        });
        return [location, filteredItems];
      })
      .filter(([, items]) => items.length > 0);
  }, [groupedItems, isFilterActive, normalizedSearch]);

  // What the folders actually render. Pure derivation — never written back.
  const displayGroups = useMemo(
    () => buildDisplayGroups(visibleLocationEntries, groupMode, resolveVendor),
    [visibleLocationEntries, groupMode, resolveVendor]
  );

  const toggleFolder = (location) => {
    const newCollapsed = new Set(collapsedFolders);
    if (newCollapsed.has(location)) {
      newCollapsed.delete(location);
    } else {
      newCollapsed.add(location);
    }
    setCollapsedFolders(newCollapsed);
  };

  // Re-sync the PAR for every count_items row of a given inventory item in the
  // session to whatever the current React state (via the ref) holds. Called
  // after autoSaveItemToDB finishes inserting a new row, so any PAR
  // edits the user made WHILE the INSERT was in flight end up on the new row.
  const resyncSharedFieldFromState = async (inventoryItemId) => {
    if (!sessionId || !inventoryItemId) return;
    const invKey = String(inventoryItemId);
    let match = null;
    const buckets = groupedItemsRef.current || {};
    for (const items of Object.values(buckets)) {
      match = (items || []).find((it) => (
        String(it.id) === invKey || String(it.inventory_item_id || '') === invKey
      ));
      if (match) break;
    }
    if (!match) return;
    const fields = {};
    if (match.par_level !== undefined) {
      fields.par_level = (match.par_level === '' || match.par_level == null)
        ? null : match.par_level;
      fields.par_unit = match.par_unit || 'each';
      fields.par_defaulted = match.par_defaulted === true;
    }
    const moq = match.min_order_qty ?? match.moq;
    if (moq !== undefined) {
      fields.min_order_qty = (moq === '' || moq == null) ? null : moq;
      fields.min_order_unit = match.min_order_unit || match.moq_unit || 'each';
    }
    if (Object.keys(fields).length === 0) return;
    await persistSharedItemFieldToSession(sessionId, inventoryItemId, fields);
  };

  // Apply an AI plan previously approved through TemplateAiAssistance. Mutates
  // React state for immediate feedback then persists the diff (removed count
  // items, newly added rows, shared par changes, and repositioning) to the
  // count_items table when running inside a live session.
  const handleApplyAiPlan = async (approvedOps) => {
    if (!Array.isArray(approvedOps) || !approvedOps.length) return;
    try {
      const beforeRows = flattenGroupedForAi(groupedItemsRef.current || groupedItems);
      const afterRows = applyAiOpsToRows(beforeRows, approvedOps, workingSetGroups);

      const beforeById = new Map();
      beforeRows.forEach((row) => {
        if (row.count_item_id) beforeById.set(row.count_item_id, row);
      });
      const afterCountItemIds = new Set();
      afterRows.forEach((row) => {
        if (row.count_item_id) afterCountItemIds.add(row.count_item_id);
      });

      const removedCountItemIds = [];
      beforeById.forEach((row, countItemId) => {
        if (!afterCountItemIds.has(countItemId)) removedCountItemIds.push(countItemId);
      });

      const additions = afterRows.filter((row) => !row.count_item_id && row._ai_added);

      // Build the PAR/MOQ writes DIRECTLY from the approved ops — never from
      // the card, which no longer offers an MOQ field at all. `set_moq` reaches
      // this editor only from an approved CounterIntelligence template-edit
      // plan (CounterIntelligenceDrawer + templates-ai-edit); it is retained so
      // an approved op is never a silent no-op, and is catalogued as a
      // still-writing legacy path in docs/moq-ownership-audit.md.
      // React state. setGroupedItems above only SCHEDULES a render; groupedItemsRef
      // is refreshed by a useEffect that cannot run while this async handler is
      // still on the stack, so reading the ref here (as resyncSharedFieldFromState
      // does) would persist the STALE pre-apply value and the reload would revert
      // the just-shown numbers. The ops already carry the authoritative new value
      // (and unit) per inventory item, and one write per inventory_item_id covers
      // every sibling location row (both are shared per item), so this is also
      // immune to which location row state happens to hold.
      const sharedFieldUpdates = new Map(); // String(invId) -> count_items patch
      const fieldsFor = (invId) => {
        const key = String(invId);
        if (!sharedFieldUpdates.has(key)) sharedFieldUpdates.set(key, { inventoryItemId: invId, fields: {} });
        return sharedFieldUpdates.get(key).fields;
      };
      approvedOps.forEach((op) => {
        const r = op.resolution || {};
        if (op.type === 'set_par') {
          const parValue = (r.par_value === '' || r.par_value == null) ? null : r.par_value;
          (r.updates || []).forEach((u) => {
            const f = fieldsFor(u.inventory_item_id);
            f.par_level = parValue;
            f.par_defaulted = false;
            // Only set the unit when the op carries one (the alcohol PAR flow does);
            // plain "set par to N" edits omit it so the existing unit is preserved.
            if (r.par_unit) f.par_unit = r.par_unit;
          });
        } else if (op.type === 'set_moq') {
          const moqValue = (r.moq_value === '' || r.moq_value == null) ? null : r.moq_value;
          (r.updates || []).forEach((u) => {
            const f = fieldsFor(u.inventory_item_id);
            f.min_order_qty = moqValue;
            if (r.moq_unit) f.min_order_unit = r.moq_unit;
          });
        }
      });

      // Render the new state immediately so the user sees the changes.
      const optimisticGrouped = regroupFromRows(afterRows);
      setGroupedItems(optimisticGrouped);
      onUpdate?.(optimisticGrouped);

      if (!sessionId) {
        notifyAi('AI changes applied in the editor. Save to persist.', 'success');
        return;
      }

      // Delete removed count_items rows.
      if (removedCountItemIds.length > 0) {
        const { error: delErr } = await supabase
          .from('count_items')
          .delete()
          .in('id', removedCountItemIds)
          .eq('count_session_id', sessionId);
        if (delErr) throw delErr;
      }

      // Insert newly added rows via autoSaveItemToDB. Fetch inventory snapshots
      // for additions whose row only carries the AI payload (not a real
      // inventory_items record).
      const additionIds = Array.from(new Set(additions
        .map((row) => row.inventory_item_id || row.id)
        .filter(Boolean)));
      const invById = new Map();
      if (additionIds.length > 0 && storeId) {
        const { data: invRows } = await supabase
          .from('inventory_items')
          .select('*')
          .eq('store_id', storeId)
          .in('id', additionIds);
        (invRows || []).forEach((row) => invById.set(String(row.id), row));
      }

      const postInsertGrouped = { ...optimisticGrouped };
      for (const addRow of additions) {
        if (typeof window.autoSaveItemToDB !== 'function') break;
        const invId = addRow.inventory_item_id || addRow.id;
        const inv = invById.get(String(invId)) || {};
        const payload = {
          ...inv,
          id: invId,
          inventory_item_id: invId,
          description: addRow.description || inv.description,
          name: addRow.name || addRow.description || inv.description,
          brand: addRow.brand ?? inv.brand,
          category: addRow.category ?? inv.category,
          vendor: addRow.vendor ?? inv.vendor,
          unit: addRow.unit || inv.unit || 'each',
          location: addRow.location,
          inventoryLocation: inv.location,
          par_level: addRow.par_level ?? null,
          par_defaulted: (addRow.par_defaulted ?? inv.par_defaulted) === true,
          par_unit: addRow.par_unit || 'each',
          min_order_qty: addRow.min_order_qty ?? null,
          min_order_unit: addRow.min_order_unit || addRow.unit || 'each',
          count_position: addRow.count_position || 1,
        };
        try {
          const saved = await window.autoSaveItemToDB(payload);
          if (saved?.id) {
            const bucket = postInsertGrouped[addRow.location] || [];
            const idx = bucket.findIndex((entry) => (
              !entry.count_item_id
              && String(entry.inventory_item_id || entry.id) === String(invId)
            ));
            if (idx !== -1) {
              bucket[idx] = { ...bucket[idx], count_item_id: saved.id };
              postInsertGrouped[addRow.location] = bucket;
            }
          }
        } catch (err) {
          console.error('❌ [ItemLocationFolders AI] autoSaveItemToDB failed:', err);
        }
      }

      setGroupedItems(postInsertGrouped);
      onUpdate?.(postInsertGrouped);

      // Persist the ops-derived fields for every affected item straight from
      // patch (one UPDATE per item, covering all of its location rows). This
      // commits to count_items BEFORE persistTemplateItemsToSession dispatches a
      // reload, and propagates the same values to the parent template / item, so
      // the applied numbers survive the refresh instead of reverting.
      for (const { inventoryItemId, fields } of sharedFieldUpdates.values()) {
        if (Object.keys(fields).length === 0) continue;
        await persistSharedItemFieldToSession(sessionId, inventoryItemId, fields);
      }

      // A par/moq-only plan moves nothing — no rows added, removed, or
      // repositioned — so the per-item field writes above ARE the entire save.
      // persistTemplateItemsToSession re-writes every row's location/position AND
      // fires refreshCountInterface, which re-renders the whole CountInterface from
      // the database. Running it for a pure PAR change is the redundant "second
      // save" that makes the values flash/reload; skip it unless the plan actually
      // changed structure. The overview's PAR state is kept current by the
      // countItemFieldsChanged events persistSharedItemFieldToSession emits
      // above, so nothing downstream needs the full refresh here.
      const changedStructure =
        removedCountItemIds.length > 0 ||
        additions.length > 0 ||
        approvedOps.some((op) => op.type !== 'set_par' && op.type !== 'set_moq');
      if (changedStructure) {
        await persistTemplateItemsToSession(sessionId, postInsertGrouped);
      }

      notifyAi('AI changes saved.', 'success');
    } catch (error) {
      console.error('❌ [ItemLocationFolders AI] Apply failed:', error);
      notifyAi(`Could not save AI changes: ${error?.message || error}`, 'error');
    }
  };

  // Expose the AI apply handler and a live view of the current flattened
  // rows so the standalone TemplateAiAssistance panel mounted outside this
  // component (see public/count.html #template-ai-panel-mount) can preview
  // and approve plans against the current count-creation state.
  useEffect(() => {
    window.applyAiPlanToCountCreation = handleApplyAiPlan;
    window.getCountCreationAiRows = () => flattenGroupedForAi(groupedItemsRef.current || {});
    window.dispatchEvent(new CustomEvent('countCreationAiRowsChanged'));
    return () => {
      if (window.applyAiPlanToCountCreation === handleApplyAiPlan) {
        delete window.applyAiPlanToCountCreation;
        // Tear the row accessor down too, so the global Counter Intelligence
        // drawer can't mistake a torn-down editor for an open template.
        delete window.getCountCreationAiRows;
      }
    };
  }, [groupedItems]);

  // Handle drag from external source (ItemDatabaseBrowser) into a location
  const handleExternalDrop = async (e, dropLocation) => {
    e.preventDefault();
    e.stopPropagation();
    const raw = e.dataTransfer.getData('application/inventory-item');
    if (!raw) return;

    let item;
    try { item = JSON.parse(raw); } catch { return; }

    console.log('🟢 [ItemLocationFolders] External drop:', item.description, '→', dropLocation);

    const next = { ...groupedItems };

    // Preserve the item's real inventory location BEFORE overwriting with the bucket
    const inventoryLocation = item.location;

    // Determine target location: use dropLocation if provided, else item's native location
    const targetLocation = dropLocation || item.location || 'Individual Items';

    // A PAR is shared across every location bucket the same inventory item
    // occupies within a template. If the item is already present in another
    // bucket with a par set, inherit it on this drop so the new location shows
    // the existing par immediately (and isn't silently reset to whatever the
    // database-browser payload carried). No minimum order quantity is inherited
    // — the drop used to default one to 1, inventing a vendor fact from nothing.
    const invId = String(item.inventory_item_id || item.id || '');
    let inheritedPar = item.par_level ?? null;
    let inheritedParDefaulted = (item.par_defaulted ?? item.item_snapshot?.par_defaulted) === true;
    if (invId) {
      for (const [loc, items] of Object.entries(next)) {
        if (loc === targetLocation) continue;
        const sibling = (items || []).find(ex => String(ex.inventory_item_id || ex.id || '') === invId);
        if (!sibling) continue;
        if (sibling.par_level != null && sibling.par_level !== '') {
          inheritedPar = sibling.par_level;
          inheritedParDefaulted = (sibling.par_defaulted ?? sibling.item_snapshot?.par_defaulted) === true;
        }
        break;
      }
    }

    // Build new item entry
    const newEntry = {
      ...item,
      location: targetLocation,
      inventoryLocation: inventoryLocation,
      templateName: targetLocation,
      source: 'database',
      par_level: inheritedPar,
      par_defaulted: inheritedParDefaulted,
      count_position: 1,
      name: item.name || item.description,
      description: item.description || item.name,
      uniqueKey: `${item.id}-drop-${Date.now()}`
    };

    // Determine drop index from pointer position within the container
    const container = e.currentTarget;
    const children = Array.from(container.querySelectorAll('[data-item-index]'));
    let insertIndex = 0;
    if (children.length > 0) {
      const y = e.clientY;
      let found = false;
      for (let i = 0; i < children.length; i++) {
        const rect = children[i].getBoundingClientRect();
        if (y < rect.top + rect.height / 2) {
          insertIndex = i;
          found = true;
          break;
        }
      }
      if (!found) insertIndex = children.length;
    }

    // Create location if it doesn't exist
    if (!next[targetLocation]) next[targetLocation] = [];

    // Prevent duplicates only inside the target location.
    // Same item may now exist in multiple locations.
    const alreadyIn = (next[targetLocation] || []).some(ex =>
      String(ex.id) === String(item.id) || String(ex.inventory_item_id) === String(item.id)
    );
    if (alreadyIn) {
      console.warn('⚠️ [ItemLocationFolders] Item already in count, skipping');
      return;
    }

    // Insert at computed position and reindex
    const locationItems = [...next[targetLocation]];
    locationItems.splice(insertIndex, 0, newEntry);
    locationItems.forEach((it, i) => { it.count_position = i + 1; });
    next[targetLocation] = locationItems;

    const nextTemplateItems = buildReindexedTemplateItems(Object.entries(next));

    // Optimistic update: render immediately
    setGroupedItems(nextTemplateItems);
    onUpdate?.(nextTemplateItems);

    // Notify the database browser so the dropped item disappears from the available list
    document.dispatchEvent(new CustomEvent('updateItemDatabaseBrowser', {
      detail: { currentItems: Object.values(nextTemplateItems).flat() }
    }));

    // Persist to DB if we have a session; rollback on failure
    if (sessionId) {
      try {
        if (typeof window.autoSaveItemToDB === 'function') {
          const savedCountItem = await window.autoSaveItemToDB({ ...newEntry, location: targetLocation });
          // Store the count_item UUID so persistTemplateItemsToSession can find the right row
          if (savedCountItem?.id) {
            const locationItems = nextTemplateItems[targetLocation] || [];
            const idx = locationItems.findIndex(it => String(it.id) === String(item.id));
            if (idx !== -1) {
              locationItems[idx] = { ...locationItems[idx], count_item_id: savedCountItem.id };
              nextTemplateItems[targetLocation] = locationItems;
              setGroupedItems({ ...nextTemplateItems });
              onUpdate?.({ ...nextTemplateItems });
            }
          }
          // Race fix: PAR edits that happened WHILE autoSaveItemToDB was
          // in-flight won't be on the just-inserted row (the INSERT used the
          // payload values as of the time the drop fired). Re-sync the row
          // using the ref, which always has the latest state.
          const invId = newEntry.inventory_item_id || newEntry.id;
          await resyncSharedFieldFromState(invId);
        }
        await persistTemplateItemsToSession(sessionId, nextTemplateItems);
      } catch (err) {
        console.error('❌ [ItemLocationFolders] Failed to persist dropped item, rolling back:', err);
        // Rollback: remove the optimistically-added item
        const rollback = { ...groupedItems };
        if (rollback[targetLocation]) {
          rollback[targetLocation] = rollback[targetLocation].filter(
            ex => String(ex.id) !== String(item.id) && String(ex.inventory_item_id) !== String(item.id)
          );
          if (rollback[targetLocation].length === 0) delete rollback[targetLocation];
        }
        const rolledBack = buildReindexedTemplateItems(Object.entries(rollback));
        setGroupedItems(rolledBack);
        onUpdate?.(rolledBack);
        // Re-expose item in the database browser
        document.dispatchEvent(new CustomEvent('updateItemDatabaseBrowser', {
          detail: { currentItems: Object.values(rolledBack).flat() }
        }));
      }
    }
  };

  const resolveCanonicalLocation = (candidate, existingLocations = []) => {
    const trimmed = String(candidate || '').trim();
    if (!trimmed) return null;
    const existingMatch = existingLocations.find(
      (locName) => String(locName || '').trim().toLowerCase() === trimmed.toLowerCase()
    );
    return existingMatch || trimmed;
  };

  const handleAddItemToLocation = async (sourceItem, _fromLocation, requestedLocation) => {
    const allKnownLocations = [
      ...Object.keys(groupedItems || {}),
      ...(storeLocations || [])
    ];
    const targetLocation = resolveCanonicalLocation(requestedLocation, allKnownLocations);
    if (!targetLocation) return;

    const next = { ...groupedItems };
    if (!next[targetLocation]) next[targetLocation] = [];

    const itemInventoryId = sourceItem.inventory_item_id || sourceItem.id;
    const existsInTarget = (next[targetLocation] || []).some((entry) => (
      String(entry.inventory_item_id || entry.id) === String(itemInventoryId)
    ));
    if (existsInTarget) return;

    // Preserve the item's real inventory location (from snapshot or top-level field)
    const inventoryLocation = sourceItem.item_snapshot?.location || sourceItem.inventoryLocation || sourceItem.location;

    const copiedEntry = {
      ...sourceItem,
      count_item_id: null,
      location: targetLocation,
      inventoryLocation: inventoryLocation,
      item_snapshot: {
        ...(sourceItem.item_snapshot || {}),
        // Do NOT overwrite location here — item_snapshot.location should reflect the
        // item's real inventory location, not the count-session bucket
      },
      uniqueKey: `${itemInventoryId}-${targetLocation}-${Date.now()}`
    };

    const targetItems = [...(next[targetLocation] || []), copiedEntry]
      .map((entry, idx) => ({ ...entry, count_position: idx + 1 }));
    next[targetLocation] = targetItems;

    const nextTemplateItems = buildReindexedTemplateItems(Object.entries(next));
    setGroupedItems(nextTemplateItems);
    onUpdate?.(nextTemplateItems);

    const canonicalLocations = [...new Set([
      ...storeLocations,
      ...Object.keys(nextTemplateItems)
    ])];
    setStoreLocations(canonicalLocations.sort((a, b) => a.localeCompare(b)));

    if (sessionId && typeof window.autoSaveItemToDB === 'function') {
      try {
        const savedCountItem = await window.autoSaveItemToDB({
          ...copiedEntry,
          id: itemInventoryId,
          inventory_item_id: itemInventoryId,
          location: targetLocation
        });
        if (savedCountItem?.id) {
          const patched = { ...nextTemplateItems };
          const idx = (patched[targetLocation] || []).findIndex((entry) => (
            String(entry.uniqueKey) === String(copiedEntry.uniqueKey)
          ));
          if (idx !== -1) {
            patched[targetLocation][idx] = {
              ...patched[targetLocation][idx],
              count_item_id: savedCountItem.id
            };
            setGroupedItems(patched);
            onUpdate?.(patched);
          }
        }
        // Race fix: PAR changes that happened while the INSERT was in
        // flight won't be on the newly inserted row. Re-sync using the latest
        // ref value so all sibling rows (including the just-inserted one) end
        // up with the current shared value.
        await resyncSharedFieldFromState(itemInventoryId);
        await persistTemplateItemsToSession(sessionId, nextTemplateItems);
      } catch (error) {
        console.error('❌ [ItemLocationFolders] Failed to add item to location:', error);
      }
    }
  };

  // Bulk version of handleAddItemToLocation: copy every item from sourceLocation
  // into requestedLocation. Mirrors the per-item logic (skip duplicates, preserve
  // inventoryLocation, reindex, autoSaveItemToDB, resync shared par) but
  // batches the optimistic state update so the UI doesn't flicker per-item.
  const handleAddAllItemsToLocation = async (sourceLocation, requestedLocation) => {
    const allKnownLocations = [
      ...Object.keys(groupedItems || {}),
      ...(storeLocations || [])
    ];
    const targetLocation = resolveCanonicalLocation(requestedLocation, allKnownLocations);
    if (!targetLocation) return;
    if (targetLocation.trim().toLowerCase() === String(sourceLocation || '').trim().toLowerCase()) return;

    const sourceItems = groupedItems[sourceLocation] || [];
    if (!sourceItems.length) return;

    const next = { ...groupedItems };
    if (!next[targetLocation]) next[targetLocation] = [];

    const existingIds = new Set(
      (next[targetLocation] || []).map((entry) => String(entry.inventory_item_id || entry.id))
    );

    const stamp = Date.now();
    const copiedEntries = [];
    sourceItems.forEach((sourceItem, idx) => {
      const itemInventoryId = sourceItem.inventory_item_id || sourceItem.id;
      if (existingIds.has(String(itemInventoryId))) return;
      existingIds.add(String(itemInventoryId));

      const inventoryLocation = sourceItem.item_snapshot?.location
        || sourceItem.inventoryLocation
        || sourceItem.location;

      copiedEntries.push({
        ...sourceItem,
        count_item_id: null,
        location: targetLocation,
        inventoryLocation,
        item_snapshot: {
          ...(sourceItem.item_snapshot || {}),
        },
        uniqueKey: `${itemInventoryId}-${targetLocation}-${stamp}-${idx}`,
      });
    });

    if (copiedEntries.length === 0) return;

    const targetItems = [...(next[targetLocation] || []), ...copiedEntries]
      .map((entry, idx) => ({ ...entry, count_position: idx + 1 }));
    next[targetLocation] = targetItems;

    const nextTemplateItems = buildReindexedTemplateItems(Object.entries(next));
    setGroupedItems(nextTemplateItems);
    onUpdate?.(nextTemplateItems);

    const canonicalLocations = [...new Set([
      ...storeLocations,
      ...Object.keys(nextTemplateItems)
    ])];
    setStoreLocations(canonicalLocations.sort((a, b) => a.localeCompare(b)));

    if (sessionId && typeof window.autoSaveItemToDB === 'function') {
      try {
        let workingItems = nextTemplateItems;
        for (const copiedEntry of copiedEntries) {
          const itemInventoryId = copiedEntry.inventory_item_id || copiedEntry.id;
          const savedCountItem = await window.autoSaveItemToDB({
            ...copiedEntry,
            id: itemInventoryId,
            inventory_item_id: itemInventoryId,
            location: targetLocation,
          });
          if (savedCountItem?.id) {
            const patched = { ...workingItems };
            const bucket = [...(patched[targetLocation] || [])];
            const idx = bucket.findIndex((entry) => (
              String(entry.uniqueKey) === String(copiedEntry.uniqueKey)
            ));
            if (idx !== -1) {
              bucket[idx] = { ...bucket[idx], count_item_id: savedCountItem.id };
              patched[targetLocation] = bucket;
              workingItems = patched;
              setGroupedItems(patched);
              onUpdate?.(patched);
            }
          }
          await resyncSharedFieldFromState(itemInventoryId);
        }
        await persistTemplateItemsToSession(sessionId, workingItems);
      } catch (error) {
        console.error('❌ [ItemLocationFolders] Failed to add all items to location:', error);
      }
    }
  };

  const handleCreateNewLocation = async (newLocationName) => {
    const trimmed = String(newLocationName || '').trim();
    if (!trimmed) return;
    // Update local state immediately
    setStoreLocations((prev) => {
      const updated = [...new Set([...prev, trimmed])].sort((a, b) => a.localeCompare(b));
      return updated;
    });
    // Persist to store_details.locations in Supabase
    try {
      const selectedStoreId = Number.parseInt(localStorage.getItem('selectedStoreId'), 10);
      if (!Number.isFinite(selectedStoreId)) return;
      const { data: detailsRows } = await supabase
        .from('store_details')
        .select('id, locations')
        .eq('store_id', selectedStoreId)
        .limit(1);
      if (!detailsRows || detailsRows.length === 0) return;
      const row = detailsRows[0];
      const existing = Array.isArray(row.locations) ? row.locations : [];
      const alreadyExists = existing.some(l => typeof l === 'string' && l.trim().toLowerCase() === trimmed.toLowerCase());
      if (alreadyExists) return;
      const updated = [...existing, trimmed];
      await supabase
        .from('store_details')
        .update({ locations: updated })
        .eq('id', row.id);
    } catch (error) {
      console.warn('⚠️ [ItemLocationFolders] Failed to persist new location:', error?.message || error);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, type } = result;
    // Hard stop, not just a UI affordance: droppableId is the bucket name and
    // flows into buildReindexedTemplateItems as the persisted location. On any
    // axis but Location that name is a category or a vendor.
    if (!isReorderable) return;

    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (type === 'LOCATION') {
      const locationEntries = Object.entries(groupedItems);
      const [movedLocation] = locationEntries.splice(source.index, 1);
      locationEntries.splice(destination.index, 0, movedLocation);
      const nextTemplateItems = buildReindexedTemplateItems(locationEntries);
      setGroupedItems(nextTemplateItems);
      onUpdate?.(nextTemplateItems);
      try {
        if (sessionId) {
          await persistTemplateItemsToSession(sessionId, nextTemplateItems);
        } else if (window.updateLocalPositions) {
          window.updateLocalPositions(nextTemplateItems);
        }
      } catch (error) {
        console.error('❌ Failed to persist location order:', error);
      }
      return;
    }

    const sourceLocation = source.droppableId;
    const destLocation = destination.droppableId;
    const nextByLocation = { ...groupedItems };
    const sourceItems = [...(nextByLocation[sourceLocation] || [])];
    const [movedItem] = sourceItems.splice(source.index, 1);
    if (!movedItem) return;

    if (sourceLocation === destLocation) {
      sourceItems.splice(destination.index, 0, movedItem);
      nextByLocation[sourceLocation] = sourceItems;
    } else {
      const destItems = [...(nextByLocation[destLocation] || [])];
      destItems.splice(destination.index, 0, { ...movedItem, location: destLocation });
      nextByLocation[sourceLocation] = sourceItems;
      nextByLocation[destLocation] = destItems;
    }

    const nextTemplateItems = buildReindexedTemplateItems(Object.entries(nextByLocation));
    setGroupedItems(nextTemplateItems);
    onUpdate?.(nextTemplateItems);
    try {
      if (sessionId) {
        await persistTemplateItemsToSession(sessionId, nextTemplateItems);
      } else if (window.updateLocalPositions) {
        window.updateLocalPositions(nextTemplateItems);
      }
    } catch (error) {
      console.error('❌ Failed to persist reordered items:', error);
    }
  };

  const getFolderIcon = (groupName) => {
    // Room emoji only make sense when the folder IS a room. On the category and
    // vendor axes a bucket named "Bar Supplies" is not a bar, so those modes get
    // one glyph for the axis instead of a bad guess per folder.
    if (!isLocationMode) {
      return (GROUP_MODES.find((mode) => mode.key === groupMode) || {}).glyph || '📁';
    }
    if (groupName.toLowerCase().includes('kitchen')) return '🍳';
    if (groupName.toLowerCase().includes('storage')) return '📦';
    if (groupName.toLowerCase().includes('prep')) return '🔪';
    if (groupName.toLowerCase().includes('office')) return '🏢';
    if (groupName.toLowerCase().includes('bar')) return '🍺';
    return '📁'; // Default folder icon
  };

  if (Object.keys(groupedItems).length === 0) {
    return (
      <div>
        <div className="text-muted text-center py-4">
          <i className="feather icon-folder" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
          <div className="mt-2">This is your template, select items from your catalog below which will appear here as your template is built.</div>
        </div>
      </div>
    );
  }

  // Handle drop of a template card dragged from the group/template picker
  const handleTemplateDrop = (e) => {
    const raw = e.dataTransfer.getData('application/count-template');
    if (!raw) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      const template = JSON.parse(raw);
      if (typeof window.addTemplateToCount === 'function') {
        window.addTemplateToCount(template);
      }
    } catch (_) { /* ignore bad JSON */ }
  };

  return (
    <>
    <style>{TEMPLATE_ITEM_CARD_CSS}</style>
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className="location-folders"
        onDragOver={(e) => { if (e.dataTransfer.types.includes('application/count-template')) e.preventDefault(); }}
        onDrop={handleTemplateDrop}
      >
        <div className="tpl-group-order" role="group" aria-label="Group Order">
          <span className="tpl-group-order-label">
            Group Order
          </span>
          {GROUP_MODES.map((mode) => {
            const isActive = groupMode === mode.key;
            return (
              <button
                key={mode.key}
                type="button"
                className="tpl-group-order-pill"
                onClick={() => handleGroupModeChange(mode.key)}
                aria-pressed={isActive}
                title={mode.key === 'location'
                  ? 'Group by storage location — the order counters walk, and the only view you can drag to reorder'
                  : `Group by ${mode.label.toLowerCase()}`}
              >
                <i className={`feather ${mode.icon}`} />
                {mode.label}
              </button>
            );
          })}
        </div>
        <div className="mb-2">
          <input
            type="text"
            className="form-control form-control-sm"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search items in this count..."
          />
          {isFilterActive && (
            <small className="text-muted">Clear search to drag and reorder items or locations.</small>
          )}
          {!isFilterActive && !isLocationMode && (
            <small className="text-muted">
              Switch to Location to drag and reorder — that order is what counters walk.
            </small>
          )}
        </div>
        {isFilterActive && displayGroups.length === 0 && (
          <div className="text-muted text-center py-3">No items match “{searchQuery.trim()}”.</div>
        )}
        <Droppable droppableId="location-order" type="LOCATION" isDropDisabled={!isReorderable}>
          {(locationDropProvided) => (
            <div ref={locationDropProvided.innerRef} {...locationDropProvided.droppableProps}>
              {displayGroups.map(([groupName, items], groupIndex) => {
                const isCollapsed = collapsedFolders.has(groupName);
                // Namespaced by axis so a category and a location that slugify
                // the same can't target each other's collapse panel.
                const folderId = `folder-${groupMode}-${groupName.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <Draggable
                    key={`group-${groupName}`}
                    draggableId={`group-${groupName}`}
                    index={groupIndex}
                    isDragDisabled={!isReorderable}
                  >
                    {(locationProvided, locationSnapshot) => (
                      <div
                        ref={locationProvided.innerRef}
                        {...locationProvided.draggableProps}
                        className="mb-2"
                        style={{
                          ...locationProvided.draggableProps.style,
                          opacity: locationSnapshot.isDragging ? 0.95 : 1
                        }}
                      >
                        <div
                          className="folder-header d-flex align-items-center justify-content-between p-2"
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}
                          onClick={() => toggleFolder(groupName)}
                          data-bs-toggle="collapse"
                          data-bs-target={`#${folderId}`}
                          aria-expanded={!isCollapsed}
                          aria-controls={folderId}
                        >
                          <div className="d-flex align-items-center">
                            {isReorderable && (
                              <span
                                {...locationProvided.dragHandleProps}
                                className="me-2 text-muted"
                                style={{ cursor: 'grab' }}
                                title="Drag to reorder location"
                              >
                                <i className="feather icon-menu" />
                              </span>
                            )}
                            <span className="me-2" style={{ fontSize: '1rem' }}>
                              {getFolderIcon(groupName)}
                            </span>
                            <span style={{ color: '#495057' }}>{groupName}</span>
                            <span className="ms-2 badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                              {items.length}
                            </span>
                          </div>
                          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            {/* "Copy everything here to another room" only means
                                something when the folder IS a room — its handler
                                indexes groupedItems by the folder name. */}
                            {isLocationMode && items.length > 0 && (
                              <LocationAddAllControl
                                location={groupName}
                                itemCount={items.length}
                                availableLocations={[...new Set([
                                  ...Object.keys(groupedItems || {}),
                                  ...storeLocations
                                ])].sort((a, b) => a.localeCompare(b))}
                                storeLocations={storeLocations}
                                onCreateLocation={handleCreateNewLocation}
                                onAddAllToLocation={handleAddAllItemsToLocation}
                              />
                            )}
                            <i
                              className={`feather icon-chevron-${isCollapsed ? 'right' : 'down'}`}
                              style={{ fontSize: '0.8rem', color: '#6c757d' }}
                            />
                          </div>
                        </div>
                        {locationSnapshot.isDragging ? (
                          <div
                            className="small p-2 border rounded mt-1"
                            style={{
                              background: 'rgba(13,110,253,0.06)',
                              borderColor: '#0d6efd',
                              color: '#0d6efd',
                              fontWeight: 500
                            }}
                          >
                            Moving: {groupName} ({items.length} {items.length === 1 ? 'item' : 'items'})
                          </div>
                        ) : (
                          <div className={`collapse ${!isCollapsed ? 'show' : ''}`} id={folderId}>
                            <Droppable droppableId={groupName} type="ITEM" isDropDisabled={!isReorderable}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className="folder-content mt-1"
                                  onDragOver={(e) => e.preventDefault()}
                                  /* A category folder is not a destination. Pass
                                     null and the drop falls back to the item's
                                     own inventory location, which is where it
                                     belongs anyway. */
                                  onDrop={(e) => handleExternalDrop(e, isLocationMode ? groupName : null)}
                                  style={{
                                    backgroundColor: snapshot.isDraggingOver ? 'rgba(13,110,253,0.05)' : 'transparent',
                                    border: snapshot.isDraggingOver ? '2px solid #0d6efd' : '2px dashed transparent',
                                    borderRadius: '6px',
                                    minHeight: items.length === 0 ? '60px' : '40px',
                                    padding: '8px',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                    position: 'relative'
                                  }}
                                >
                                  {snapshot.isDraggingOver && items.length === 0 && (
                                    <div
                                      style={{
                                        textAlign: 'center',
                                        color: '#0d6efd',
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        padding: '12px',
                                        fontStyle: 'italic',
                                        pointerEvents: 'none'
                                      }}
                                    >
                                      ↓ Drop here to add to {groupName}
                                    </div>
                                  )}
                                  {snapshot.isDraggingOver && items.length > 0 && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: '3px',
                                      backgroundColor: '#0d6efd',
                                      borderRadius: '3px 3px 0 0',
                                      pointerEvents: 'none'
                                    }} />
                                  )}
                                  {items.map((item, index) => (
                                    <Draggable
                                      /* Keyed on the item's REAL location, not
                                         the folder: one item counted in two
                                         rooms lands in a single category folder
                                         twice, and duplicate draggableIds tear
                                         down the whole DragDropContext. */
                                      key={`${item.count_item_id || item.id}-${item.location}`}
                                      draggableId={`${item.count_item_id || item.id}-${item.location}`}
                                      index={index}
                                      isDragDisabled={!isReorderable}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          className="mb-2"
                                          data-item-index={index}
                                          style={{
                                            ...provided.draggableProps.style,
                                            opacity: snapshot.isDragging ? 0.65 : 1,
                                            boxShadow: snapshot.isDragging
                                              ? '0 8px 24px rgba(13,110,253,0.25)'
                                              : undefined,
                                            transform: snapshot.isDragging
                                              ? `${provided.draggableProps.style?.transform || ''} rotate(1deg)`
                                              : provided.draggableProps.style?.transform,
                                            zIndex: snapshot.isDragging ? 1000 : 'auto'
                                          }}
                                        >
                                          <ItemCard
                                            item={item}
                                            /* The item's REAL location, never the
                                               display folder. onRemove hands this
                                               back and the parent indexes
                                               groupedItems with it — a category
                                               name there would delete the row from
                                               the database and leave it on screen. */
                                            location={item.location}
                                            draggable={isReorderable}
                                            index={index}
                                            orderCalculationsEnabled={orderCalculationsEnabled}
                                            resolveVendor={resolveVendor}
                                            purchaseOptions={
                                              pooledOptionsByItemId[item.inventory_item_id ?? item.item_snapshot?.id] || []
                                            }
                                            groupMemberCount={(() => {
                                              const members = groupMembersFor(workingSetGroups, item.inventory_item_id ?? item.item_snapshot?.id);
                                              return members ? members.size : 0;
                                            })()}
                                            selectedOptionId={
                                              selectedOptionByCountItemId[item.count_item_id] ?? null
                                            }
                                            vendorNameById={vendorNameById}
                                            onSelectVendor={handleSelectVendor}
                                            ladderRungs={
                                              ladderRungsByItemId[String(item.inventory_item_id ?? item.item_snapshot?.id)] || []
                                            }
                                            availableLocations={[...new Set([
                                              ...Object.keys(groupedItems || {}),
                                              ...storeLocations
                                            ])].sort((a, b) => a.localeCompare(b))}
                                            storeLocations={storeLocations}
                                            groupedItems={groupedItems}
                                            onCreateLocation={handleCreateNewLocation}
                                            onAddToLocation={handleAddItemToLocation}
                                            onUpdatePar={(itemId, loc, value, unit, normalization) => {
                                              // PAR is shared across every location bucket that
                                              // holds the same inventory item: setting a par in one
                                              // room also applies in every other room the item
                                              // occupies. It's a visual cue (one number per item),
                                              // not a per-room total (12 in Bar + 12 in Kitchen is
                                              // still 12, not 24).
                                              // Match on both id AND inventory_item_id so newly-
                                              // added sibling rows (whose id may be the inventory
                                              // UUID while pre-existing rows carry the same value
                                              // under a different field) still get picked up.
                                              const target = String(itemId);
                                              const source = item;
                                              // Only a true inventory FK — source.id is the
                                              // count_template_items PK and would not match
                                              // count_template_items.inventory_item_id. The item
                                              // snapshot's id IS the inventory id, so it backstops
                                              // rows born without the FK (a missing FK used to
                                              // silently swallow the PAR edit: shown locally,
                                              // never persisted).
                                              const invId = source.inventory_item_id
                                                || (source.item_snapshot && source.item_snapshot.id)
                                                || null;
                                              const newTemplateItems = { ...groupedItems };
                                              Object.keys(newTemplateItems).forEach((room) => {
                                                newTemplateItems[room] = newTemplateItems[room].map(i => {
                                                  const match = String(i.id) === target
                                                    || String(i.inventory_item_id || '') === target
                                                    || (invId && (String(i.id) === String(invId)
                                                      || String(i.inventory_item_id || '') === String(invId)));
                                                  return match ? {
                                                    ...i,
                                                    par_level: value,
                                                    par_defaulted: false,
                                                    par_unit: unit,
                                                    ...parNormalizationPatch(normalization),
                                                  } : i;
                                                });
                                              });
                                              setGroupedItems(newTemplateItems);
                                              window.currentTemplateItems = newTemplateItems;
                                              onUpdate?.(newTemplateItems);
                                              // Persist to DB so new counts and reloads see the
                                              // updated value without waiting for an explicit Save.
                                              if (sessionId && invId) {
                                                persistSharedItemFieldToSession(sessionId, invId, {
                                                  par_level: value === '' || value == null ? null : value,
                                                  par_defaulted: false,
                                                  par_unit: unit || 'each',
                                                  ...parNormalizationPatch(normalization),
                                                }).then((propagationResult) => {
                                                  const msg = formatPropagationToast(propagationResult);
                                                  if (msg) {
                                                    showTemplateSyncToast(msg);
                                                  } else if (propagationResult && !propagationResult.parPropagated) {
                                                    showTemplateSyncToast('PAR saved to count — template could not be updated');
                                                  }
                                                }).catch(() => {});
                                              }
                                            }}
                                            onRemove={async (removedItem, loc) => {
                                              const inventoryId = removedItem?.id;
                                              // count_items.id is the row UUID; falls back to (session, inventory_id)
                                              // when count_item_id wasn't stamped onto the local item.
                                              const countItemId = removedItem?.count_item_id || null;

                                              if (sessionId) {
                                                let query = supabase.from('count_items').delete();
                                                if (countItemId) {
                                                  query = query.eq('id', countItemId);
                                                } else {
                                                  query = query
                                                    .eq('count_session_id', sessionId)
                                                    .eq('inventory_item_id', inventoryId);
                                                }
                                                const { error: delErr } = await query;
                                                if (delErr) {
                                                  console.error('❌ [ItemLocationFolders] Failed to delete count_item:', delErr);
                                                  return; // don't remove from UI if DB delete failed
                                                }
                                              }
                                              const nextByLocation = { ...groupedItems };
                                              if (nextByLocation[loc]) {
                                                nextByLocation[loc] = nextByLocation[loc].filter(i => String(i.id) !== String(inventoryId));
                                                if (nextByLocation[loc].length === 0) delete nextByLocation[loc];
                                                const nextTemplateItems = buildReindexedTemplateItems(Object.entries(nextByLocation));
                                                setGroupedItems(nextTemplateItems);
                                                onUpdate?.(nextTemplateItems);

                                                // Tell the database browser to re-add this item to available list
                                                document.dispatchEvent(new CustomEvent('updateItemDatabaseBrowser', {
                                                  detail: { currentItems: Object.values(nextTemplateItems).flat() }
                                                }));

                                                if (sessionId) {
                                                  await persistTemplateItemsToSession(sessionId, nextTemplateItems);
                                                }
                                              }
                                            }}
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {locationDropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
    {templateSyncToast && (
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2f6f3a',
          color: '#fff',
          padding: '8px 18px',
          borderRadius: '20px',
          fontSize: '0.82rem',
          fontWeight: 600,
          zIndex: 9999,
          pointerEvents: 'none',
          maxWidth: '85vw',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
        }}
      >
        {templateSyncToast}
      </div>
    )}
    </>
  );
};

export default ItemLocationFolders;
